const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";

// --- Identity (RBAC headers) ----------------------------------------------
// The backend resolves the caller from trusted gateway headers (X-User /
// X-Role). In dev the workbench injects a senior_analyst identity; the value
// is persisted to localStorage so it survives reloads (FR-033, R7).
export interface Identity {
  username: string;
  role: string;
}

const DEFAULT_IDENTITY: Identity = { username: "analyst", role: "senior_analyst" };

export function getIdentity(): Identity {
  if (typeof window === "undefined") return DEFAULT_IDENTITY;
  try {
    const raw = window.localStorage.getItem("slpra.identity");
    return raw ? (JSON.parse(raw) as Identity) : DEFAULT_IDENTITY;
  } catch {
    return DEFAULT_IDENTITY;
  }
}

export function setIdentity(identity: Identity): void {
  if (typeof window !== "undefined") {
    window.localStorage.setItem("slpra.identity", JSON.stringify(identity));
  }
}

// --- Auth token -------------------------------------------------------------
// Real authentication: POST /api/auth/login issues a signed bearer token,
// persisted to localStorage and attached as `Authorization: Bearer` on every
// request. X-User/X-Role are still sent so a dev gateway (auth_required=false)
// keeps working; under enforcement the backend resolves identity from the token.
const TOKEN_KEY = "slpra.token";

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null): void {
  if (typeof window === "undefined") return;
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  else window.localStorage.removeItem(TOKEN_KEY);
}

export function identityHeaders(): Record<string, string> {
  const id = getIdentity();
  const headers: Record<string, string> = { "X-User": id.username, "X-Role": id.role };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

export interface LoginResult {
  token: string;
  username: string;
  role: string;
  display_name?: string | null;
}

/** Authenticate; on success persist token + identity, then return the result. */
export async function login(username: string, password: string): Promise<LoginResult> {
  // 登录端点开放且仅读 body，不需身份头；显式只带 Content-Type。
  const loginHeaders = { "Content-Type": "application/json" };
  const res = await fetch(`${API_BASE}/api/auth/login`, {
    method: "POST",
    headers: loginHeaders,
    body: JSON.stringify({ username, password }),
  });
  if (!res.ok) {
    let detail = "用户名或密码错误";
    try {
      const parsed = JSON.parse(await res.text());
      detail = parsed.detail ?? detail;
    } catch {
      /* keep default */
    }
    throw new Error(detail);
  }
  const result = (await res.json()) as LoginResult;
  setToken(result.token);
  setIdentity({ username: result.username, role: result.role });
  return result;
}

/** Clear the local session (best-effort server notify; always clears locally). */
export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/api/auth/logout`, { method: "POST", headers: identityHeaders() });
  } catch {
    /* ignore network / expired-token errors — local clear is what matters */
  }
  setToken(null);
}

/** Raised when a write hits an optimistic-concurrency conflict (HTTP 409). */
export class VersionConflictError extends Error {
  status = 409;
  currentVersion: number | null;
  constructor(message: string, currentVersion: number | null) {
    super(message);
    this.name = "VersionConflictError";
    this.currentVersion = currentVersion;
  }
}

export class HarnessDisplayNotReadyError extends Error {
  readonly status = 409;
  readonly code = "HARNESS_DISPLAY_NOT_READY";

  constructor() {
    super("结果暂未就绪，请刷新");
    this.name = "HarnessDisplayNotReadyError";
  }
}

export async function fetchAPI<T>(path: string, options?: RequestInit): Promise<T> {
  const isMultipart =
    typeof FormData !== "undefined" && options?.body instanceof FormData;
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      ...(isMultipart ? {} : { "Content-Type": "application/json" }),
      ...identityHeaders(),
      ...Object.fromEntries(new Headers(options?.headers).entries()),
    },
  });
  if (!res.ok) {
    const body = await res.text();
    if (res.status === 401) {
      // 令牌失效/缺失：清本地会话并跳登录（登录页自身不跳，防循环）。
      setToken(null);
      if (typeof window !== "undefined" && window.location.pathname !== "/login") {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.replace(`/login?next=${next}`);
      }
      throw new Error("未认证：请重新登录");
    }
    if (res.status === 409) {
      let current: number | null = null;
      let message = body;
      let harnessDisplayNotReady = false;
      try {
        const parsed = JSON.parse(body);
        const detail = parsed.detail ?? parsed.error ?? parsed;
        current = detail?.current_revision ?? detail?.current_version ?? null;
        message = detail?.message ?? body;
        harnessDisplayNotReady = /^\/api\/document-analysis\/runs\/[^/]+\/harness-graph$/.test(path)
          && detail?.code === "HARNESS_DISPLAY_NOT_READY";
      } catch {
        /* keep raw body */
      }
      if (harnessDisplayNotReady) throw new HarnessDisplayNotReadyError();
      throw new VersionConflictError(message, current);
    }
    throw new Error(`API ${res.status}: ${body}`);
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

const jsonBody = (data: unknown): RequestInit => ({ body: JSON.stringify(data) });

// Ontology
export const getModules = (signal?: AbortSignal) => fetchAPI<Module[]>("/api/ontology/modules", { signal });
export const getClassHierarchy = (module: string) =>
  fetchAPI<TreeNode[]>(`/api/ontology/${module}/classes`);

// Entities
export const searchEntities = (params: Record<string, string>, signal?: AbortSignal) => {
  const qs = new URLSearchParams(params).toString();
  return fetchAPI<EntitySearchResult>(`/api/entities?${qs}`, { signal });
};
export const getEntity = (iri: string, signal?: AbortSignal) =>
  fetchAPI<Individual>(`/api/entities/${encodeURIComponent(iri)}`, { signal });

// Reasoning
export const runAssessment = (data: AssessmentRequest) =>
  fetchAPI<AssessmentResponse>("/api/reasoning/assess", {
    method: "POST", body: JSON.stringify(data),
  });
export const calculatePDE = (data: PDERequest) =>
  fetchAPI<PDEResponse>("/api/reasoning/calculate/pde", {
    method: "POST", body: JSON.stringify(data),
  });
export const calculateMACO = (data: MACORequest) =>
  fetchAPI<MACOResult>("/api/reasoning/calculate/maco", {
    method: "POST", body: JSON.stringify(data),
  });

// Knowledge Graph
export const getKGStats = () => fetchAPI<KGStats>("/api/kg/stats");
export const runSPARQL = (query: string) =>
  fetchAPI<Record<string, unknown>[]>("/api/kg/sparql", {
    method: "POST", body: JSON.stringify({ query }),
  });

// --- Integration realtime (能力三) -----------------------------------------
export interface Connector {
  id: string;
  system_type: string;
  name: string;
  ingest_mode: string;
  poll_interval_seconds: number;
  connection_config: Record<string, unknown> | null;
  field_mapping: Record<string, unknown> | null;
  is_active: boolean;
  last_status: string | null;
  last_error: string | null;
}

export interface MaterializationRun {
  id: string;
  connector_id: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  cursor_from: Record<string, unknown> | null;
  cursor_to: Record<string, unknown> | null;
  change_count: number;
  changes: Array<Record<string, unknown>> | null;
  event_ids: string[] | null;
  error_message: string | null;
}

export interface DashboardData {
  compatibility_matrix: Array<{
    equipment: string | null; product: string | null;
    risk_level: string | null; conclusion_id: string;
  }>;
  schedule_risks: Array<{
    date: string | null; equipment: string | null;
    conflict: boolean; detail: string;
  }>;
  updated_at: string;
}

export interface RuleTrace {
  rules_fired: Array<Record<string, unknown>>;
}

export const listConnectors = () =>
  fetchAPI<Connector[]>("/api/integration/connectors");
export const createConnector = (data: Partial<Connector>) =>
  fetchAPI<Connector>("/api/integration/connectors", {
    method: "POST", body: JSON.stringify(data),
  });
export const deleteConnector = (id: string) =>
  fetchAPI<void>(`/api/integration/connectors/${id}`, { method: "DELETE" });
export const testConnector = (id: string) =>
  fetchAPI<{ ok: boolean; latency_ms: number | null; error: string | null }>(
    `/api/integration/connectors/${id}/test`, { method: "POST" });
export const syncConnector = (id: string) =>
  fetchAPI<{ run_id: string; status: string }>(
    `/api/integration/connectors/${id}/sync`, { method: "POST" });
export const listConnectorRuns = (id: string) =>
  fetchAPI<{ runs: MaterializationRun[] }>(`/api/integration/connectors/${id}/runs`);
export const getDashboard = () =>
  fetchAPI<DashboardData>("/api/integration/dashboard");
const MOCK_TRACES: Record<string, RuleTrace> = {
  "concl-pde-ibuprofen-001": {
    rules_fired: [
      { rule: "PDE_ORAL_ROUTE", substance: "布洛芬 (Ibuprofen)", dose: "4 mg/day", route: "口服", LD50: "636 mg/kg", NOAEL: "40 mg/kg/day", safety_factor: 100 },
      { rule: "OEB_CLASSIFICATION", OEB: "OEB-3", basis: "PDE 4.0 mg/day，NOAEL < 50 mg/kg/day" },
    ],
  },
  "concl-maco-aspirin-002": {
    rules_fired: [
      { rule: "MACO_SHARED_EQUIPMENT", substance: "阿司匹林 (Aspirin)", PDE: "4 mg/day", min_batch_size: "100 kg", MACO: "0.04 mg per swab", equipment: "反应釜 R-201" },
      { rule: "CLEANING_LIMIT", residue_limit: "≤ 10 ppm", method: "HPLC-UV", LOQ: "0.5 ppm" },
    ],
  },
  "concl-cleaning-reactor-003": {
    rules_fired: [
      { rule: "CLEANING_VALIDATION", equipment: "反应釜 R-301", product_A: "氯沙坦钾", product_B: "二甲双胍盐酸盐", acceptance: "≤ 10 ppm 残留", swab_locations: 6, rinse_cycles: 3 },
      { rule: "VISUAL_INSPECTION", result: "合格", inspector: "张工 (QC)" },
    ],
  },
  "concl-stability-losartan-004": {
    rules_fired: [
      { rule: "ICH_STABILITY", substance: "氯沙坦钾 (Losartan Potassium)", condition: "40°C/75%RH 加速 6 月", assay: "98.2%", degradation: "< 0.5%", conclusion: "稳定性合格" },
    ],
  },
  "concl-pde-metformin-005": {
    rules_fired: [
      { rule: "PDE_ORAL_ROUTE", substance: "二甲双胍盐酸盐 (Metformin HCl)", dose: "25 mg/day", route: "口服", NOAEL: "250 mg/kg/day", safety_factor: 100 },
      { rule: "OEB_CLASSIFICATION", OEB: "OEB-1", basis: "PDE 25 mg/day，低毒性" },
    ],
  },
  "concl-cross-contam-006": {
    rules_fired: [
      { rule: "CROSS_CONTAMINATION_RISK", shared_line: "固体制剂车间 Line-3", products: "5 品种共线", highest_OEB: "OEB-5", control: "密闭投料 + HEPA + 独立空调" },
      { rule: "RISK_MATRIX", severity: 5, probability: 2, RPN: 10, action: "需 QA 逐批放行审核" },
    ],
  },
  "concl-maco-omeprazole-007": {
    rules_fired: [
      { rule: "MACO_SHARED_EQUIPMENT", substance: "奥美拉唑 (Omeprazole)", PDE: "2.4 mg/day", min_batch_size: "50 kg", MACO: "0.048 mg per swab", equipment: "混合机 M-102" },
    ],
  },
};

export const getConclusionTrace = (id: string) =>
  fetchAPI<RuleTrace>(`/api/reasoning/conclusions/${id}/trace`).catch(
    () => MOCK_TRACES[id] ?? { rules_fired: [] },
  );

// --- 研发文档事实源 doc_repo（能力三 / 007）--------------------------------
// 复用既有 connector CRUD 与 /api/entities 检索（不新建路由/检索框架）。
export const DOCUMENT_NS = "https://ontology.pharma-gmp.cn/slpra/document/";

/** 研发阶段受控词表（skos:notation 1–6 定序；与 slpra-document.ttl 一一对应）。 */
export const DEVELOPMENT_PHASES: Array<{ iri: string; label: string; notation: string }> = [
  { iri: `${DOCUMENT_NS}Phase_DrugDiscovery`, label: "药物发现", notation: "1" },
  { iri: `${DOCUMENT_NS}Phase_Preclinical`, label: "临床前", notation: "2" },
  { iri: `${DOCUMENT_NS}Phase_ClinicalI`, label: "临床Ⅰ期", notation: "3" },
  { iri: `${DOCUMENT_NS}Phase_ClinicalII_III`, label: "临床Ⅱ/Ⅲ期", notation: "4" },
  { iri: `${DOCUMENT_NS}Phase_NDA_BLA`, label: "NDA/BLA 申报", notation: "5" },
  { iri: `${DOCUMENT_NS}Phase_PostMarket`, label: "上市后", notation: "6" },
];

/** 研发阶段 IRI → 中文标签（未知回退 local-name）。 */
export const phaseLabel = (iri: string | null | undefined): string => {
  if (!iri) return "—";
  const hit = DEVELOPMENT_PHASES.find((p) => p.iri === iri);
  return hit ? hit.label : iri.split("#").flatMap((part) => part.split("/")).pop() || iri;
};

/** 研发阶段中文标签 → IRI（左侧选中的阶段分类 → 上传信封 development_phase）。 */
export const phaseIriByLabel = (label: string | null | undefined): string | null => {
  if (!label) return null;
  return DEVELOPMENT_PHASES.find((p) => p.label === label)?.iri ?? null;
};

/** drug-development 命名空间（RegulatoryDocument 更细分子类所在，非 /document/）。 */
export const DRUG_DEV_NS = "https://ontology.pharma-gmp.cn/slpra/drug-development/";

export interface DocTypeOption {
  /** 类 local-name（上传信封 doc_type，即 doc_type_to_class 覆盖的键）。 */
  localName: string;
  /** 完整类 IRI（下发 doc_type_to_class 覆盖 → 物化为此文档类）。 */
  iri: string;
  label: string;
}

/**
 * 受控文档类型 = 本体中 `RegulatoryDocument` 的全部子类（上传后由用户指定）。
 * 分组与标签逐字取自本体 TTL：`slpra-document.ttl`（/document/ 命名空间 6 子类）
 * 与 `slpra-drug-development.ttl`（/drug-development/ 命名空间 23 子类，按研发阶段分章）。
 * 后端经连接器 `doc_type_to_class` 覆盖 + 强制 module=document 物化（跨命名空间无碍）。
 */
export const DOCUMENT_TYPE_GROUPS: Array<{ group: string; options: DocTypeOption[] }> = [
  {
    group: "通用法规文档",
    options: [
      { localName: "RegulatoryDocument", iri: `${DOCUMENT_NS}RegulatoryDocument`, label: "法规文档" },
      { localName: "INDDossier", iri: `${DOCUMENT_NS}INDDossier`, label: "IND 申报资料" },
      { localName: "TechTransferReport", iri: `${DOCUMENT_NS}TechTransferReport`, label: "技术转移报告" },
      { localName: "ProcessValidationReport", iri: `${DOCUMENT_NS}ProcessValidationReport`, label: "工艺验证报告" },
      { localName: "StabilityReport", iri: `${DOCUMENT_NS}StabilityReport`, label: "稳定性报告" },
      { localName: "NDA_BLADossier", iri: `${DOCUMENT_NS}NDA_BLADossier`, label: "NDA/BLA 申报资料" },
      { localName: "PVReport", iri: `${DOCUMENT_NS}PVReport`, label: "药物警戒报告" },
    ],
  },
  {
    group: "发现与临床前",
    options: [
      { localName: "TargetValidationReport", iri: `${DRUG_DEV_NS}TargetValidationReport`, label: "靶点验证报告" },
      { localName: "HTSReport", iri: `${DRUG_DEV_NS}HTSReport`, label: "高通量筛选报告" },
      { localName: "LeadOptimizationReport", iri: `${DRUG_DEV_NS}LeadOptimizationReport`, label: "先导化合物优化报告" },
      { localName: "CandidateSelectionReport", iri: `${DRUG_DEV_NS}CandidateSelectionReport`, label: "候选药物遴选报告" },
      { localName: "PharmacologyStudyReport", iri: `${DRUG_DEV_NS}PharmacologyStudyReport`, label: "药理学研究报告" },
      { localName: "PKStudyReport", iri: `${DRUG_DEV_NS}PKStudyReport`, label: "药代动力学研究报告" },
      { localName: "ToxicologyStudyReport", iri: `${DRUG_DEV_NS}ToxicologyStudyReport`, label: "毒理学研究报告" },
      { localName: "CMCReport", iri: `${DRUG_DEV_NS}CMCReport`, label: "CMC 报告" },
    ],
  },
  {
    group: "临床",
    options: [
      { localName: "InvestigatorBrochure", iri: `${DRUG_DEV_NS}InvestigatorBrochure`, label: "研究者手册（IB）" },
      { localName: "ClinicalTrialProtocol", iri: `${DRUG_DEV_NS}ClinicalTrialProtocol`, label: "临床试验方案" },
      { localName: "InformedConsentForm", iri: `${DRUG_DEV_NS}InformedConsentForm`, label: "知情同意书（ICF）" },
      { localName: "ClinicalStudyReport", iri: `${DRUG_DEV_NS}ClinicalStudyReport`, label: "临床试验报告（CSR）" },
      { localName: "ClinicalOverview", iri: `${DRUG_DEV_NS}ClinicalOverview`, label: "临床概述" },
      { localName: "ClinicalSummary", iri: `${DRUG_DEV_NS}ClinicalSummary`, label: "临床总结" },
    ],
  },
  {
    group: "NDA/BLA（CTD 模块）",
    options: [
      { localName: "CTDModule1", iri: `${DRUG_DEV_NS}CTDModule1`, label: "CTD 模块1（行政信息）" },
      { localName: "CTDModule2", iri: `${DRUG_DEV_NS}CTDModule2`, label: "CTD 模块2（综述）" },
      { localName: "CTDModule3", iri: `${DRUG_DEV_NS}CTDModule3`, label: "CTD 模块3（药学）" },
      { localName: "CTDModule4", iri: `${DRUG_DEV_NS}CTDModule4`, label: "CTD 模块4（非临床）" },
      { localName: "CTDModule5", iri: `${DRUG_DEV_NS}CTDModule5`, label: "CTD 模块5（临床）" },
    ],
  },
  {
    group: "上市后",
    options: [
      { localName: "PSUR", iri: `${DRUG_DEV_NS}PSUR`, label: "定期安全性更新报告（PSUR/PBRER）" },
      { localName: "ADRReport", iri: `${DRUG_DEV_NS}ADRReport`, label: "药品不良反应报告" },
      { localName: "RMPDocument", iri: `${DRUG_DEV_NS}RMPDocument`, label: "风险管理计划文档" },
      { localName: "PostApprovalChangeApplication", iri: `${DRUG_DEV_NS}PostApprovalChangeApplication`, label: "上市后变更申请" },
    ],
  },
];

const _ALL_DOC_TYPES: DocTypeOption[] = DOCUMENT_TYPE_GROUPS.flatMap((g) => g.options);

/** 扁平：文档类型 local-name → 中文标签（docTypeLabel 用；覆盖两命名空间全部子类）。 */
export const DOC_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  _ALL_DOC_TYPES.map((o) => [o.localName, o.label]),
);

/** 扁平：文档类型 local-name → 完整类 IRI（上传时下发 doc_type_to_class 覆盖用）。 */
export const DOC_TYPE_CLASS_IRI: Record<string, string> = Object.fromEntries(
  _ALL_DOC_TYPES.map((o) => [o.localName, o.iri]),
);

/** 默认文档类型（上传类型选择器的初始值）。 */
export const DEFAULT_DOC_TYPE = "RegulatoryDocument";

export const docTypeLabel = (classIri: string | null | undefined): string => {
  if (!classIri) return "—";
  const ln = classIri.split("#").flatMap((part) => part.split("/")).pop() || classIri;
  return DOC_TYPE_LABELS[ln] || ln;
};

export type DocRepoAccessMode = "inline" | "upload" | "http";

export interface DocRepoConnectorInput {
  name: string;
  accessMode: DocRepoAccessMode;
  pollIntervalSeconds?: number;
  /** http 模式：EDMS/eTMF 端点 URL。 */
  baseUrl?: string;
  /** http 模式：凭据**环境变量名**引用（如 "EDMS_TOKEN"）——绝不传明文 token（FR-010）。 */
  tokenRef?: string;
  apiKeyRef?: string;
  /** inline 模式：归一化变更骨架数组。 */
  inlineChanges?: Array<Record<string, unknown>>;
  /** upload 模式：文档上传信封数组。 */
  uploadPayload?: Array<Record<string, unknown>>;
  /** 字段映射覆盖（如 doc_type_to_class：文档类型 local-name → 完整文档类 IRI）。 */
  fieldMapping?: Record<string, unknown>;
}

/** 据接入模式构建 doc_repo 的 connection_config（凭据**仅以变量名引用**入库，无明文）。 */
export const buildDocRepoConfig = (input: DocRepoConnectorInput): Record<string, unknown> => {
  if (input.accessMode === "http") {
    const cfg: Record<string, unknown> = { access_mode: "http", base_url: input.baseUrl || "" };
    if (input.tokenRef) cfg.token_ref = input.tokenRef;
    if (input.apiKeyRef) cfg.api_key_ref = input.apiKeyRef;
    return cfg;
  }
  if (input.accessMode === "upload") {
    return { access_mode: "upload", upload_payload: input.uploadPayload || [] };
  }
  return { access_mode: "inline", inline_changes: input.inlineChanges || [] };
};

/** 创建 doc_repo 连接器（复用既有 createConnector；system_type 固定 doc_repo）。 */
export const createDocRepoConnector = (input: DocRepoConnectorInput) =>
  createConnector({
    name: input.name || "研发文档事实源",
    system_type: "doc_repo",
    ingest_mode: "poll",
    poll_interval_seconds: input.pollIntervalSeconds ?? 2,
    connection_config: buildDocRepoConfig(input),
    ...(input.fieldMapping ? { field_mapping: input.fieldMapping } : {}),
  });

// --- 研发文档上传（报告中心 · reports）------------------------------------
// 复用 doc_repo `upload` 接入模式：构造**标准上传信封**（doc_id/doc_type/version/
// title/metadata，与后端 `_normalize_upload` 契约逐字段一致）→ 建连接器 → **立即触发
// 一次同步**（创建不自动物化、轮询器默认关闭）。同步返回后，文档已作为托管文档个体
// （facts#<doc_id>，class=document/<docType>）落库，可经 listDocuments 检索、按类型归类。
//
// 关键：文档个体 IRI 可**预测**（FACTS_NS + doc_id）——前端据此在上传瞬间乐观占位、
// 并在后端列出后按 key 去重对账，无需臆造上传端点，也不受列表刷新时序影响。

/** A-Box 事实个体命名空间（须与后端 materializer `_FACT_BASE_IRI` 保持一致）。 */
export const FACTS_NS = "http://slpra.org/facts#";

/** 一次待上传文件的已构造信封 + 预测坐标（供乐观占位/对账）。 */
export interface PreparedUpload {
  docId: string;
  /** 预测的文档个体 IRI（= FACTS_NS + docId）。 */
  iri: string;
  title: string;
  /** 用户指定的文档类型 local-name（DOC_TYPE_CLASS_IRI 的键）。 */
  docType: string;
  /** 该文档类型的完整类 IRI（下发 doc_type_to_class 覆盖 → 决定物化文档类）。 */
  classIri: string;
  /** 左侧选中的研发阶段 IRI（落 metadata.hasDevelopmentPhase → 按阶段归类）；未选为 null。 */
  phaseIri: string | null;
  size: number;
  /** 原始文件对象（供 submitUpload 保存可预览原件或进入结构化抽取）。 */
  file: File;
  /** 可预览/抽取源类型（word/excel）；其他类型为 null。 */
  sourceType: string | null;
  /** 提交给后端的上传信封（doc_repo `_normalize_upload` 契约）。 */
  envelope: Record<string, unknown>;
}

/** 文件扩展名 → 可保存预览或执行结构化抽取的源类型。 */
function annotationSourceType(name: string): string | null {
  const ext = name.toLowerCase().split(".").pop() || "";
  if (ext === "docx" || ext === "doc") return "word";
  if (ext === "xlsx" || ext === "xls") return "excel";
  return null;
}

function newDocId(): string {
  const rand =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
  return `upload-${rand}`;
}

/**
 * 纯函数：把一个待上传文件构造为标准上传信封 + 预测坐标（不发起任何请求）。
 * `docType`（用户上传后指定）须为 DOC_TYPE_CLASS_IRI 的键，其决定 class_iri → 类型列；
 * `phaseIri`（左侧选中的研发阶段）落 metadata.hasDevelopmentPhase → 阶段归类（文件夹）
 * 且与后端 `hasDevelopmentPhase`（阶段检索/继承的规范谓词）对齐。
 */
export function prepareUpload(
  file: File,
  opts: { docType: string; phaseIri?: string | null },
): PreparedUpload {
  const docId = newDocId();
  const stamp = new Date().toISOString();
  const docType = opts.docType;
  const phaseIri = opts.phaseIri ?? null;
  const classIri = DOC_TYPE_CLASS_IRI[docType] || `${DOCUMENT_NS}${docType}`;
  const metadata: Record<string, unknown> = {
    created_at: stamp,
    ingested_at: stamp,
    content_type: file.type || "application/octet-stream",
    file_size: file.size,
    approvalStatus: "approved",
    sourceSystem: "web-upload",
  };
  // 研发阶段 → 文档属性 → 按阶段归类；键用规范谓词 hasDevelopmentPhase（与后端
  // search_entities 阶段过滤 + _document_phase 继承同键，避免历史 development_phase 漂移）。
  if (phaseIri) metadata.hasDevelopmentPhase = phaseIri;
  return {
    docId,
    iri: `${FACTS_NS}${docId}`,
    title: file.name,
    docType,
    classIri,
    phaseIri,
    size: file.size,
    file,
    sourceType: annotationSourceType(file.name),
    envelope: {
      doc_id: docId,
      doc_type: docType,
      version: 1,
      title: file.name,
      metadata,
    },
  };
}

/**
 * 提交已构造的上传信封：
 *   1) Word 只保存为 doc_repo 的结构预览源，绝不从上传动作启动旧关系识别；Excel 仍走
 *      显式映射的结构化抽取。返回的 job_id 回写上传信封，供详情页读取原文预览。
 *   2) 建 doc_repo（upload 模式）连接器 → **立即触发一次同步**，物化为可检索文档个体。
 * 同步为同步调用（run_sync 落库后才返回），故本函数 resolve 时文档已可被 listDocuments 检索。
 * 抽取任务创建失败不阻断入库（预览/实体降级为不可用，文档仍可见、可归类）。
 */
export async function submitUpload(prepared: PreparedUpload[]): Promise<void> {
  if (prepared.length === 0) return;

  for (const p of prepared) {
    if (!p.sourceType) continue;
    try {
      const job = await createAutoExtractionJob({
        file: p.file,
        source_type: p.sourceType,
        doc_class_iri: p.classIri,
        purpose: p.sourceType === "word" ? "doc_repo_preview" : undefined,
      });
      const meta = (p.envelope.metadata ?? {}) as Record<string, unknown>;
      meta.job_id = job.id;  // → 物化落 properties_json.job_id → resolveDocumentContent 解析预览
      p.envelope.metadata = meta;
    } catch {
      // 抽取失败不阻断文档入库；预览与关联实体优雅降级为不可用。
    }
  }

  const names = prepared.map((p) => p.title).join("、");
  // 为所选文档类型下发 doc_type_to_class 覆盖（localName → 完整类 IRI）：使非默认命名空间
  // （drug-development）的 RegulatoryDocument 子类也能正确物化并强制归 module=document。
  const docTypeToClass: Record<string, string> = {};
  for (const p of prepared) docTypeToClass[p.docType] = p.classIri;
  const connector = await createDocRepoConnector({
    name: `文档上传：${names}`.slice(0, 120),
    accessMode: "upload",
    uploadPayload: prepared.map((p) => p.envelope),
    fieldMapping: { doc_type_to_class: docTypeToClass },
  });
  // 创建连接器不自动物化（轮询器默认关闭）——显式触发一次同步，立即物化为文档个体。
  await syncConnector(connector.id);
}

// --- 通用 REST/JSON 源连接器 rest_api（014 US2）----------------------------
// 声明驱动抽取的 api_endpoint 源经此连接器分页拉取；凭据**仅以环境变量名引用**入库
// （FR-006），base_url 须为内网地址（FR-022，后端 422 兜底校验）。

export type RestAuthScheme = "bearer" | "api_key";
export type RestPaginationStyle = "cursor" | "offset" | "page";

export interface RestApiConnectorInput {
  name: string;
  /** 内网 base_url（公网/云端将被后端拒绝，FR-022）。 */
  baseUrl: string;
  endpoint: string;
  authScheme: RestAuthScheme;
  /** 凭据**环境变量名**引用（bearer→token_env / api_key→api_key_env）——绝不传明文（FR-006）。 */
  tokenEnv?: string;
  apiKeyEnv?: string;
  /** api_key 模式的自定义请求头名（默认 X-API-Key）。 */
  apiKeyHeader?: string;
  paginationStyle?: RestPaginationStyle;
  cursorPath?: string;
  pageSize?: number;
  pollIntervalSeconds?: number;
}

/** 据输入构建 rest_api 的 connection_config（凭据**仅以变量名引用**，无明文入库）。 */
export const buildRestApiConfig = (input: RestApiConnectorInput): Record<string, unknown> => {
  const auth: Record<string, unknown> = { scheme: input.authScheme };
  if (input.authScheme === "bearer" && input.tokenEnv) auth.token_env = input.tokenEnv;
  if (input.authScheme === "api_key") {
    if (input.apiKeyEnv) auth.api_key_env = input.apiKeyEnv;
    if (input.apiKeyHeader) auth.header = input.apiKeyHeader;
  }
  const style = input.paginationStyle ?? "cursor";
  const pagination: Record<string, unknown> = { style, page_size: input.pageSize ?? 200 };
  if (style === "cursor") pagination.cursor_path = input.cursorPath || "$.next";
  return {
    base_url: input.baseUrl,
    endpoint: input.endpoint,
    auth,
    pagination,
  };
};

/** 创建 rest_api 连接器（复用既有 createConnector；system_type 固定 rest_api）。 */
export const createRestApiConnector = (input: RestApiConnectorInput) =>
  createConnector({
    name: input.name || "内网 REST 源",
    system_type: "rest_api",
    ingest_mode: "poll",
    poll_interval_seconds: input.pollIntervalSeconds ?? 2,
    connection_config: buildRestApiConfig(input),
  });

// --- 数据库源连接器 database ------------------------------------------------

export interface DatabaseConnectorInput {
  name: string;
  /** 数据库 DSN 的**环境变量名**（如 SOURCE_DB_DSN）——绝不传明文连接串（FR-006）。 */
  dsnEnv: string;
  /** 可选：仅反射指定 schema。 */
  schema?: string;
  /** 可选：仅反射指定表。 */
  includeTables?: string[];
  pollIntervalSeconds?: number;
}

export const buildDatabaseConfig = (
  input: DatabaseConnectorInput,
): Record<string, unknown> => {
  const cfg: Record<string, unknown> = { dsn_env: input.dsnEnv };
  if (input.schema) cfg.schema = input.schema;
  if (input.includeTables?.length) cfg.include_tables = input.includeTables;
  return cfg;
};

/** 创建 database 连接器（复用既有 createConnector；system_type 固定 database）。 */
export const createDatabaseConnector = (input: DatabaseConnectorInput) =>
  createConnector({
    name: input.name || "数据库源",
    system_type: "database",
    ingest_mode: "poll",
    poll_interval_seconds: input.pollIntervalSeconds ?? 2,
    connection_config: buildDatabaseConfig(input),
  });

// --- 文件源连接器 file （Excel / Word / PDF）-------------------------------

export type FileFormat = "excel" | "word" | "pdf";

export interface FileConnectorInput {
  name: string;
  /** 文件绝对路径或 glob 模式（如 /data/imports/*.xlsx）。 */
  filePath: string;
  /** 文件格式（留空则按扩展名自动推断）。 */
  format?: FileFormat;
  /** Excel 工作表名（可选，默认第一个）。 */
  sheetName?: string;
  pollIntervalSeconds?: number;
}

export const buildFileConfig = (
  input: FileConnectorInput,
): Record<string, unknown> => {
  const cfg: Record<string, unknown> = { file_path: input.filePath };
  if (input.format) cfg.format = input.format;
  if (input.sheetName) cfg.sheet_name = input.sheetName;
  return cfg;
};

/** 创建 file 连接器（复用既有 createConnector；system_type 固定 file）。 */
export const createFileConnector = (input: FileConnectorInput) =>
  createConnector({
    name: input.name || "文件源",
    system_type: "file",
    ingest_mode: "poll",
    poll_interval_seconds: input.pollIntervalSeconds ?? 2,
    connection_config: buildFileConfig(input),
  });

/** 列出研发文档个体（module=document），可按研发阶段过滤（US3 FR-005）。 */
export const listDocuments = (developmentPhaseIri?: string, pageSize = 100, signal?: AbortSignal) => {
  const params: Record<string, string> = { module: "document", page_size: String(pageSize) };
  if (developmentPhaseIri) params.development_phase = developmentPhaseIri;
  return searchEntities(params, signal);
};
export const deleteDocument = (iri: string) =>
  fetchAPI<void>(`/api/entities/${encodeURIComponent(iri)}`, { method: "DELETE" });

/** 上传文档 → 抽取任务 id 的候选键（`submitUpload` 落 `job_id`；兼容历史别名）。 */
const DOCUMENT_JOB_KEYS = [
  "job_id", "jobId", "source_job_id", "extraction_job_id", "hasJob", "sourceJob",
] as const;

/**
 * 解析某研发文档个体关联的抽取任务 id（上传时 `submitUpload` 落入
 * `properties_json.job_id`）。用于「文档 → 生成风险评估报告 / 在线预览」等按 jobId
 * 取数的场景。取不到（文档未走标注管线，或字段缺失）时返回 null，调用方据此优雅降级。
 */
export async function resolveDocumentJobId(iri: string): Promise<string | null> {
  if (!iri) return null;
  const docs = await listDocuments();
  const shadow = docs.items.find((d) => d.iri === iri);
  const props = shadow?.properties_json ?? {};
  for (const key of DOCUMENT_JOB_KEYS) {
    const value = props[key];
    if (typeof value === "string" && value) return value;
  }
  return null;
}

// --- Compliance (能力六) ----------------------------------------------------
export interface PendingConclusion {
  id: string;
  risk_level: string | null;
  execution_type: string;
}
export const getPendingSignatures = () =>
  fetchAPI<{ conclusions: PendingConclusion[] }>("/api/compliance/signatures/pending");
export const signConclusion = (data: {
  conclusion_id: string; username: string; password: string; meaning: string;
}) =>
  fetchAPI<{ signature_id: string; conclusion_id: string; effective: boolean; signed_at: string }>(
    "/api/compliance/signatures", { method: "POST", body: JSON.stringify(data) });

// QA 拒绝（Part 11 重认证 + 原因）→ 既有后端端点 compliance.reject_conclusion。
export interface RejectRequest {
  conclusion_id: string; username: string; password: string; reason: string;
}
export interface RejectResponse {
  conclusion_id: string; lifecycle_state: string; voided_actions: number;
}
export const rejectConclusion = (req: RejectRequest) =>
  fetchAPI<RejectResponse>("/api/compliance/reject", {
    method: "POST", body: JSON.stringify(req),
  });

// --- Extraction (能力二) ----------------------------------------------------
export const listExtractionConfigs = () =>
  fetchAPI<ExtractionConfig[]>("/api/extraction/configs");
export const listExtractionJobs = () =>
  fetchAPI<ExtractionJob[]>("/api/extraction/jobs");
export const getExtractionJob = (id: string, signal?: AbortSignal) =>
  fetchAPI<ExtractionJob>(`/api/extraction/jobs/${id}`, { signal });

export async function createExtractionJob(params: {
  source_type: string; config_id: string; file?: File; db_source?: object;
}): Promise<ExtractionJob> {
  const fd = new FormData();
  fd.append("source_type", params.source_type);
  fd.append("config_id", params.config_id);
  if (params.file) fd.append("file", params.file);
  if (params.db_source) fd.append("db_source", JSON.stringify(params.db_source));
  const res = await fetch(`${API_BASE}/api/extraction/jobs`, {
    method: "POST", headers: identityHeaders(), body: fd,
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.json();
}

/** Subscribe to job progress via SSE. Returns an unsubscribe fn. */
export function subscribeJobProgress(
  jobId: string, onEvent: (e: JobProgressEvent) => void,
): () => void {
  const id = getIdentity();
  // EventSource cannot set headers; pass identity + token as query. 强制态下
  // 后端 get_current_user_sse 认 ?token=；未强制时 x_user/x_role 走网关路径。
  const token = getToken();
  const tokenQs = token ? `&token=${encodeURIComponent(token)}` : "";
  const url = `${API_BASE}/api/extraction/jobs/${jobId}/progress?x_user=${id.username}&x_role=${id.role}${tokenQs}`;
  const es = new EventSource(url);
  es.onmessage = (ev) => {
    try { onEvent(JSON.parse(ev.data) as JobProgressEvent); } catch { /* ignore */ }
  };
  return () => es.close();
}

// --- Extraction types (能力二) ---------------------------------------------
export interface ExtractionConfig {
  id: string;
  name: string;
  target_class_iri: string;
  source_type: string;
  column_mapping?: Record<string, string> | null;
  llm_prompt_template?: string | null;
  is_active?: boolean;
}
export interface ExtractionJob {
  id: string;
  source_type: string;
  source_mode?: string | null;
  source_filename: string | null;
  document_path: string | null;
  status: string;
  total_candidates: number;
  approved_count: number;
  rejected_count: number;
  error_message: string | null;
  created_at: string;
}
export interface JobProgressEvent {
  job_id: string;
  run_id?: string;
  stage: string;
  annotation_stage?: string;
  pct: number;
  status: string;
  degraded: boolean;
  started_at?: number;
  updated_at?: number;
  tasks_processed?: number;
  tasks_completed?: number;
  tasks_failed?: number;
  model_calls?: number;
  http_attempts?: number;
  model_request?: ModelRequestProgress;
  data_revision?: number;
  candidate_count?: number;
  first_relationship_seconds?: number;
  stage_statistics?: Record<string, Record<string, number>>;
  has_checkpoint?: boolean;
  can_resume?: boolean;
  pause_requested?: boolean;
}

export interface ModelRequestProgress {
  request_id: string;
  logical_call_id: string;
  attempt: number;
  status: string;
  stage: string;
  created_at: number;
  started_at?: number | null;
  deadline_at: number;
  queue_seconds?: number;
  request_seconds?: number;
  elapsed_seconds?: number;
  prompt_tokens?: number;
  cache_tokens?: number;
  error_code?: string;
}

export interface AnnotationRunReceipt {
  job_id: string;
  run_id: string;
  status: "queued" | "resumed" | "restarted";
  has_checkpoint: boolean;
}

export async function pauseAnnotation(jobId: string): Promise<void> {
  await annotationControl(jobId, "pause");
}

export async function resumeAnnotation(jobId: string): Promise<AnnotationRunReceipt> {
  return annotationControl(jobId, "resume");
}

export async function rerunAnnotation(jobId: string, templateId?: string): Promise<AnnotationRunReceipt> {
  return annotationControl(jobId, "rerun", templateId);
}

function annotationControl(jobId: string, action: "pause"): Promise<{ status: "pause_requested" }>;
function annotationControl(jobId: string, action: "resume" | "rerun", templateId?: string): Promise<AnnotationRunReceipt>;
async function annotationControl(jobId: string, action: "pause" | "resume" | "rerun", templateId?: string) {
  const query = templateId ? `?template_id=${encodeURIComponent(templateId)}` : "";
  const response = await fetch(`${API_BASE}/api/extraction/jobs/${jobId}/annotation/${action}${query}`, {
    method: "POST",
    headers: identityHeaders(),
  });
  if (!response.ok) {
    const label = { pause: "暂停识别", resume: "继续识别", rerun: "重新识别" }[action];
    let message = `${label}请求失败（${response.status}）`;
    try {
      const body = await response.json();
      if (typeof body.detail === "string") message = body.detail;
    } catch { /* A proxy error may have a non-JSON body. */ }
    throw new Error(message);
  }
  return response.json();
}

// Types
export interface Module {
  key: string; iri: string; label: string | null;
  class_count: number; individual_count: number;
}
export interface TreeNode {
  iri: string; name: string; label: string | null;
  individual_count: number; children: TreeNode[];
}
export interface Individual {
  iri: string; name: string; class_iris: string[];
  label_zh: string | null; label_en: string | null;
  properties: Record<string, unknown>;
}
export interface EntityShadow {
  iri: string; class_iri: string; label_zh: string | null;
  label_en: string | null; module: string; properties_json: Record<string, unknown> | null;
}
export interface EntitySearchResult {
  items: EntityShadow[]; total: number; page: number; page_size: number;
}
export interface AssessmentRequest { drug_iri: string; equipment_iris: string[]; assessment_type?: string; }
export interface AssessmentResponse {
  drug_iri: string; equipment_iris: string[];
  risk_level: string | null; rules_fired: RuleFired[];
  scenarios: ScenarioResult[]; requires_dedication: boolean;
  maco: MACOResult | null; recommendations: string[];
}
export interface RuleFired {
  rule_id: string; rule_group: string; description: string;
  inputs: Record<string, unknown>; conclusion: Record<string, unknown>;
  regulation_ref?: string;
}
export interface ScenarioResult { scenario_iri: string; scenario_name: string; requirements: Record<string, unknown>; }
export interface PDERequest { pod: number; bw?: number; f1?: number; f2?: number; f3?: number; f4?: number; f5?: number; mf?: number; }
export interface PDEResponse { pde_value: number; parameters: Record<string, number>; }
export interface MACORequest { pde?: number; mbs: number; tdd_next: number; min_therapeutic_dose?: number; ld50?: number; route?: string; }
export interface MACOResult { maco_value: number; method_used: string; all_methods: Record<string, number>; unit?: string; }
export interface KGStats { total_entities: number; by_module: Record<string, number>; by_class: Record<string, number>; }
export interface GraphNode { id: string; label: string | null; type: string; module: string | null; }
export interface GraphEdge { source: string; target: string; label: string; }

// ===========================================================================
// T-Box 维护工作台（能力一）—— 可编辑元数据 API（契约 §2–§11）
// ===========================================================================

// --- E1 class --------------------------------------------------------------
export interface TBoxRestriction {
  id: string; kind: string; property_iri: string | null;
  property_kind: string | null; filler_iri: string | null;
  cardinality: number | null; version: number; status: string;
}
export interface TBoxMapping {
  id: string; class_iri: string | null; mapping_type: string;
  target: string; source_system: string | null; health: string;
  query_config?: MockQueryConfig | null;
  version: number; status: string;
}
export interface TBoxClass {
  id: string; slpra_iri: string; label: string; comment: string | null;
  module: string | null; parent_iri: string | null; bfo_category: string | null;
  field_schema: Record<string, unknown> | null; status: string; version: number;
  is_reviewed: boolean; is_disabled: boolean; confidence: number | null;
  restrictions: TBoxRestriction[]; mappings: TBoxMapping[];
  created_at: string | null; updated_at: string | null;
}
export interface ClassCreateInput {
  slpra_iri: string; label: string; comment?: string | null;
  module?: string | null; parent_iri?: string | null; bfo_category?: string | null;
}
export interface ClassUpdateInput {
  expected_version: number; label?: string | null; comment?: string | null;
  module?: string | null; parent_iri?: string | null; bfo_category?: string | null;
}

export const createClass = (data: ClassCreateInput) =>
  fetchAPI<TBoxClass>("/api/ontology/classes", { method: "POST", ...jsonBody(data) });
export const updateClass = (iri: string, data: ClassUpdateInput) =>
  fetchAPI<TBoxClass>(`/api/ontology/classes/${encodeURIComponent(iri)}`, {
    method: "PUT", ...jsonBody(data),
  });
export const deleteClass = (iri: string, expectedVersion: number) =>
  fetchAPI<void>(
    `/api/ontology/classes/${encodeURIComponent(iri)}?expected_version=${expectedVersion}`,
    { method: "DELETE" },
  );
export const disableClass = (iri: string, expectedVersion: number) =>
  fetchAPI<TBoxClass>(`/api/ontology/classes/${encodeURIComponent(iri)}/disable`, {
    method: "POST", ...jsonBody({ expected_version: expectedVersion }),
  });
export const reviewClass = (iri: string, expectedVersion: number) =>
  fetchAPI<TBoxClass>(`/api/ontology/classes/${encodeURIComponent(iri)}/review`, {
    method: "POST", ...jsonBody({ expected_version: expectedVersion }),
  });
export const getTBoxClass = (iri: string) =>
  fetchAPI<TBoxClass>(`/api/ontology/classes/${encodeURIComponent(iri)}`);

// --- E2 link type ----------------------------------------------------------
export type PropertyMultiplicity = "unspecified" | "single" | "multiple";
export interface PropertyCardinality {
  multiplicity: PropertyMultiplicity;
  min_cardinality: number | null;
  max_cardinality: number | null;
}
export interface TBoxLinkType extends PropertyCardinality {
  id: string; slpra_iri: string; label: string; comment: string | null;
  domain_iri: string | null; range_iri: string | null; inverse_iri: string | null;
  is_functional: boolean; is_symmetric: boolean; is_transitive: boolean;
  status: string; version: number; is_disabled: boolean;
  inherited_from_iri?: string | null; inherited_from_label?: string | null;
}
export interface LinkTypeInput extends Partial<PropertyCardinality> {
  slpra_iri?: string; label?: string; comment?: string | null;
  domain_iri?: string | null; range_iri?: string | null; inverse_iri?: string | null;
  is_functional?: boolean; is_symmetric?: boolean; is_transitive?: boolean;
  expected_version?: number;
}
export const listLinkTypes = (domainIri?: string, includeInherited = false) =>
  fetchAPI<TBoxLinkType[]>(`/api/ontology/link-types${propQuery(domainIri, includeInherited)}`);
export const createLinkType = (data: LinkTypeInput) =>
  fetchAPI<TBoxLinkType>("/api/ontology/link-types", { method: "POST", ...jsonBody(data) });
export const updateLinkType = (iri: string, data: LinkTypeInput) =>
  fetchAPI<TBoxLinkType>(`/api/ontology/link-types/${encodeURIComponent(iri)}`, {
    method: "PUT", ...jsonBody(data),
  });
export const deleteLinkType = (iri: string, expectedVersion: number) =>
  fetchAPI<void>(
    `/api/ontology/link-types/${encodeURIComponent(iri)}?expected_version=${expectedVersion}`,
    { method: "DELETE" },
  );

// --- E3 data property ------------------------------------------------------
export interface TBoxDataProperty extends PropertyCardinality {
  id: string; slpra_iri: string; label: string; comment: string | null;
  domain_iri: string | null; datatype: string; unit: string | null;
  controlled_vocab: Record<string, unknown> | null;
  status: string; version: number; is_disabled: boolean;
  inherited_from_iri?: string | null; inherited_from_label?: string | null;
}
export interface DataPropertyInput extends Partial<PropertyCardinality> {
  slpra_iri?: string; label?: string; comment?: string | null;
  domain_iri?: string | null; datatype?: string; unit?: string | null;
  controlled_vocab?: Record<string, unknown> | null; expected_version?: number;
}
export interface RiskVocabulary { key: string; label: string; values: string[]; }

// Shared query builder for domain-scoped property listings (relations + data props).
const propQuery = (domainIri?: string, includeInherited = false) => {
  if (!domainIri) return "";
  const p = new URLSearchParams({ domain_iri: domainIri });
  if (includeInherited) p.set("include_inherited", "true");
  return `?${p.toString()}`;
};
export const listDataProperties = (domainIri?: string, includeInherited = false) =>
  fetchAPI<TBoxDataProperty[]>(`/api/ontology/data-properties${propQuery(domainIri, includeInherited)}`);
export const createDataProperty = (data: DataPropertyInput) =>
  fetchAPI<TBoxDataProperty>("/api/ontology/data-properties", { method: "POST", ...jsonBody(data) });
export const updateDataProperty = (iri: string, data: DataPropertyInput) =>
  fetchAPI<TBoxDataProperty>(`/api/ontology/data-properties/${encodeURIComponent(iri)}`, {
    method: "PUT", ...jsonBody(data),
  });
export const deleteDataProperty = (iri: string, expectedVersion: number) =>
  fetchAPI<void>(
    `/api/ontology/data-properties/${encodeURIComponent(iri)}?expected_version=${expectedVersion}`,
    { method: "DELETE" },
  );
export const getRiskVocabularies = () =>
  fetchAPI<RiskVocabulary[]>("/api/ontology/risk-vocabularies");

// --- E4 action -------------------------------------------------------------
export interface TBoxAction {
  id: string; slpra_iri: string; label: string; comment: string | null;
  actor_iri: string | null; target_iri: string | null;
  precondition: Record<string, unknown> | null;
  postcondition: Record<string, unknown> | null;
  params: Record<string, unknown> | null;
  status: string; version: number; is_disabled: boolean;
}
export interface ActionInput {
  slpra_iri?: string; label?: string; comment?: string | null;
  actor_iri?: string | null; target_iri?: string | null;
  precondition?: Record<string, unknown> | null;
  postcondition?: Record<string, unknown> | null;
  params?: Record<string, unknown> | null; expected_version?: number;
}
export const getActions = () => fetchAPI<TBoxAction[]>("/api/ontology/actions");
export const createAction = (data: ActionInput) =>
  fetchAPI<TBoxAction>("/api/ontology/actions", { method: "POST", ...jsonBody(data) });

// --- E5 restriction --------------------------------------------------------
export interface RestrictionInput {
  kind?: string; property_iri?: string | null; property_kind?: string | null;
  filler_iri?: string | null; cardinality?: number | null; expected_version?: number;
}
export const createRestriction = (classIri: string, data: RestrictionInput) =>
  fetchAPI<TBoxRestriction>(
    `/api/ontology/classes/${encodeURIComponent(classIri)}/restrictions`,
    { method: "POST", ...jsonBody(data) },
  );
export const updateRestriction = (id: string, data: RestrictionInput) =>
  fetchAPI<TBoxRestriction>(`/api/ontology/restrictions/${id}`, {
    method: "PUT", ...jsonBody(data),
  });
export const deleteRestriction = (id: string, expectedVersion: number) =>
  fetchAPI<void>(`/api/ontology/restrictions/${id}?expected_version=${expectedVersion}`, {
    method: "DELETE",
  });

// --- E6 mapping + health ---------------------------------------------------
export interface MappingInput {
  mapping_type: string; target: string; source_system?: string | null;
  query_config?: MockQueryConfig | null;
  expected_version?: number;
}
export interface MappingHealth {
  ok: string[]; unmapped: string[]; drift: string[]; orphan: string[];
}
export const getMappings = (classIri: string) =>
  fetchAPI<TBoxMapping[]>(`/api/ontology/classes/${encodeURIComponent(classIri)}/mappings`);
export const createMapping = (classIri: string, data: MappingInput) =>
  fetchAPI<TBoxMapping>(`/api/ontology/classes/${encodeURIComponent(classIri)}/mappings`, {
    method: "POST", ...jsonBody(data),
  });
export const updateMapping = (id: string, data: MappingInput) =>
  fetchAPI<TBoxMapping>(`/api/ontology/mappings/${id}`, { method: "PUT", ...jsonBody(data) });
export const deleteMapping = (id: string, expectedVersion: number) =>
  fetchAPI<void>(`/api/ontology/mappings/${id}?expected_version=${expectedVersion}`, {
    method: "DELETE",
  });
export const getMappingHealth = () =>
  fetchAPI<MappingHealth>("/api/ontology/mappings/health");

// --- E6b property bindings (014 declaration-driven mapping) -----------------
/** Source-entity mapping types whose columns/fields drive extraction. */
export const SOURCE_ENTITY_MAPPING_TYPES = ["db_table", "api_endpoint"];
export interface PropertyBinding {
  id: string; class_mapping_id: string; property_iri: string;
  property_kind: string; source_path: string;
  transform_type: string; transform_config: Record<string, unknown> | null;
  is_identifier: boolean; is_label: boolean;
  object_resolution: string | null; target_class_iri: string | null;
  target_id_path: string | null; nested_binding_id: string | null;
  version: number; status: string;
}
export interface PropertyBindingInput {
  property_iri: string; property_kind?: string; source_path: string;
  transform_type?: string; transform_config?: Record<string, unknown> | null;
  is_identifier?: boolean; is_label?: boolean;
  object_resolution?: string | null; target_class_iri?: string | null;
  target_id_path?: string | null; nested_binding_id?: string | null;
  expected_version?: number;
}
export interface BindingValidationReport {
  health: string; errors: ValidationIssue[]; warnings: ValidationIssue[];
}
export const getPropertyBindings = (mappingId: string) =>
  fetchAPI<PropertyBinding[]>(`/api/ontology/mappings/${mappingId}/property-bindings`);
export const createPropertyBinding = (mappingId: string, data: PropertyBindingInput) =>
  fetchAPI<PropertyBinding>(`/api/ontology/mappings/${mappingId}/property-bindings`, {
    method: "POST", ...jsonBody(data),
  });
export const updatePropertyBinding = (id: string, data: PropertyBindingInput) =>
  fetchAPI<PropertyBinding>(`/api/ontology/property-bindings/${id}`, {
    method: "PUT", ...jsonBody(data),
  });
export const deletePropertyBinding = (id: string, expectedVersion: number) =>
  fetchAPI<void>(`/api/ontology/property-bindings/${id}?expected_version=${expectedVersion}`, {
    method: "DELETE",
  });
export const validateBinding = (mappingId: string) =>
  fetchAPI<BindingValidationReport>(`/api/ontology/mappings/${mappingId}/validate`, {
    method: "POST",
  });

// --- §8 validation ---------------------------------------------------------
export interface ValidationIssue { code: string; message: string; entity_iri: string | null; }
export interface ValidationReport {
  blocking: ValidationIssue[]; warnings: ValidationIssue[];
  reasoner: { ran: boolean; consistent: boolean | null; note: string | null };
}
export const validateOntology = () =>
  fetchAPI<ValidationReport>("/api/ontology/validate", { method: "POST" });

// --- §9 import / export / diff ---------------------------------------------
export interface DiffResult {
  turtle_preview: string; triples_added: string[]; triples_removed: string[];
}
export interface ImportResult { added: number; updated: number; conflicts: string[]; }
export const exportTTL = async (): Promise<string> => {
  const res = await fetch(`${API_BASE}/api/ontology/export/ttl`, { headers: identityHeaders() });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.text();
};
export const exportDiff = () => fetchAPI<DiffResult>("/api/ontology/export/diff");
export const importTTL = (content: string) =>
  fetchAPI<ImportResult>("/api/ontology/import/ttl", { method: "POST", ...jsonBody({ content }) });

// --- §10 release -----------------------------------------------------------
export interface ReleaseSummary {
  id: string; release_no: string; title: string; status: string;
  ttl_commit_sha: string | null; published_at: string | null; created_at: string | null;
}
export interface ChangeLogItem {
  id: string; entity_table: string; entity_id: string; change_kind: string;
  before: Record<string, unknown> | null; after: Record<string, unknown> | null;
}
export interface ReleaseDetail extends ReleaseSummary {
  ttl_diff: string | null;
  validation_report: ValidationReport | null;
  change_log: ChangeLogItem[];
}
export const getReleases = () => fetchAPI<ReleaseSummary[]>("/api/ontology/releases");
export const getRelease = (id: string) => fetchAPI<ReleaseDetail>(`/api/ontology/releases/${id}`);
export const createRelease = (title: string) =>
  fetchAPI<ReleaseDetail>("/api/ontology/releases", { method: "POST", ...jsonBody({ title }) });
export const submitRelease = (id: string) =>
  fetchAPI<ReleaseDetail>(`/api/ontology/releases/${id}/submit`, { method: "POST" });
export const publishRelease = (id: string) =>
  fetchAPI<ReleaseDetail>(`/api/ontology/releases/${id}/publish`, { method: "POST" });
export const rollbackRelease = (id: string) =>
  fetchAPI<ReleaseDetail>(`/api/ontology/releases/${id}/rollback`, { method: "POST" });

// ===========================================================================
// 声明式规则层 (能力六 / spec 006) — E11/E12/E13 可版本化规则数据 (US3, T041)
//
// 规则即数据：判据阈值 / 决策规则 / 冲突策略全部走与 T-Box 一致的
// fetchAPI + 乐观并发(expected_version) 通道。模式（pattern/antecedent）是
// 受限词汇的 AST——见 RulePattern；前端表单只暴露解释器 VOCABULARY 内的算子。
// ===========================================================================

/** 解释器受限模式 AST 节点（与 backend interpreter.VOCABULARY 对齐）。 */
export type RulePattern =
  | { op: "some_values_from"; property: string; filler_class: string }
  | { op: "class_membership"; property: string; classes: string[] }
  | { op: "datatype_facet"; property: string; cmp: PatternCmp; value: number }
  | { op: "boolean_has_value"; property: string; value: boolean }
  | { op: "external_alignment"; property: string; alignment: string }
  | { op: "class_present"; class: string }
  | { op: "literal_eq"; key: string; value: unknown }
  | { op: "literal_cmp"; key: string; cmp: PatternCmp; value: unknown }
  | { op: "and"; operands: RulePattern[] }
  | { op: "or"; operands: RulePattern[] };

export type PatternCmp = "gt" | "ge" | "lt" | "le" | "eq" | "ne";
/** 受限表单直接暴露的算子（叶子节点，单一谓词/阈值），排除 and/or 复合与底层 literal_*。 */
export const PATTERN_OPS = [
  "datatype_facet",
  "boolean_has_value",
  "class_membership",
  "some_values_from",
  "external_alignment",
  "class_present",
] as const;
export const PATTERN_CMP_OPS: PatternCmp[] = ["gt", "ge", "lt", "le", "eq", "ne"];

// --- E11 分类判据 (充要定义) ------------------------------------------------
export interface TBoxClassificationCriterion {
  id: string; criterion_key: string;
  target_class_iri: string | null; target_class_label: string | null;
  pattern: RulePattern; regulation_ref: string | null; logic_role: string;
  status: string; version: number; is_disabled: boolean;
  created_at: string | null; updated_at: string | null;
}
export interface CriterionCreateInput {
  criterion_key: string; target_class_iri: string; pattern: RulePattern;
  regulation_ref?: string | null; logic_role?: string;
}
export interface CriterionUpdateInput {
  expected_version: number; target_class_iri?: string | null;
  pattern?: RulePattern; regulation_ref?: string | null;
  logic_role?: string | null; is_disabled?: boolean | null;
}
export const listClassificationCriteria = () =>
  fetchAPI<TBoxClassificationCriterion[]>("/api/ontology/classification-criteria");
export const createClassificationCriterion = (data: CriterionCreateInput) =>
  fetchAPI<TBoxClassificationCriterion>("/api/ontology/classification-criteria", {
    method: "POST", ...jsonBody(data),
  });
export const updateClassificationCriterion = (key: string, data: CriterionUpdateInput) =>
  fetchAPI<TBoxClassificationCriterion>(
    `/api/ontology/classification-criteria/${encodeURIComponent(key)}`,
    { method: "PUT", ...jsonBody(data) },
  );
export const deleteClassificationCriterion = (key: string, expectedVersion: number) =>
  fetchAPI<void>(
    `/api/ontology/classification-criteria/${encodeURIComponent(key)}?expected_version=${expectedVersion}`,
    { method: "DELETE" },
  );
export const publishClassificationCriterion = (key: string, expectedVersion: number) =>
  fetchAPI<TBoxClassificationCriterion>(
    `/api/ontology/classification-criteria/${encodeURIComponent(key)}/publish`,
    { method: "POST", ...jsonBody({ expected_version: expectedVersion }) },
  );

// --- E12 决策规则 (产生式 R-ED / R-SC / R-CP) -------------------------------
export type DecisionRuleGroup =
  | "equipment_dedication" | "scenario_identification" | "contamination_risk" | "risk_assessment";
export const DECISION_RULE_GROUPS: DecisionRuleGroup[] = [
  "equipment_dedication", "scenario_identification", "contamination_risk", "risk_assessment",
];
export interface TBoxDecisionRule {
  id: string; slpra_iri: string; rule_key: string; rule_group: DecisionRuleGroup;
  antecedent: RulePattern; consequent: Record<string, unknown>; priority: number;
  regulation_ref: string | null; label: string; comment: string | null;
  status: string; version: number; is_disabled: boolean;
  created_at: string | null; updated_at: string | null;
}
export interface DecisionRuleCreateInput {
  rule_key: string; rule_group: DecisionRuleGroup; antecedent: RulePattern;
  consequent: Record<string, unknown>; priority?: number;
  regulation_ref?: string | null; label?: string | null; comment?: string | null;
}
export interface DecisionRuleUpdateInput {
  expected_version: number; rule_group?: DecisionRuleGroup | null;
  antecedent?: RulePattern; consequent?: Record<string, unknown>;
  priority?: number | null; regulation_ref?: string | null;
  label?: string | null; comment?: string | null; is_disabled?: boolean | null;
}
export const listDecisionRules = (ruleGroup?: DecisionRuleGroup) =>
  fetchAPI<TBoxDecisionRule[]>(
    `/api/ontology/decision-rules${ruleGroup ? `?rule_group=${ruleGroup}` : ""}`,
  );
export const createDecisionRule = (data: DecisionRuleCreateInput) =>
  fetchAPI<TBoxDecisionRule>("/api/ontology/decision-rules", { method: "POST", ...jsonBody(data) });
export const updateDecisionRule = (key: string, data: DecisionRuleUpdateInput) =>
  fetchAPI<TBoxDecisionRule>(`/api/ontology/decision-rules/${encodeURIComponent(key)}`, {
    method: "PUT", ...jsonBody(data),
  });
export const deleteDecisionRule = (key: string, expectedVersion: number) =>
  fetchAPI<void>(
    `/api/ontology/decision-rules/${encodeURIComponent(key)}?expected_version=${expectedVersion}`,
    { method: "DELETE" },
  );
export const publishDecisionRule = (key: string, expectedVersion: number) =>
  fetchAPI<TBoxDecisionRule>(
    `/api/ontology/decision-rules/${encodeURIComponent(key)}/publish`,
    { method: "POST", ...jsonBody({ expected_version: expectedVersion }) },
  );

// --- E13 冲突消解策略 (固定维度集，仅 GET/PUT) ------------------------------
export interface TBoxConflictPolicy {
  id: string; slpra_iri: string; dimension: string; strategy: string;
  priority_lattice: Record<string, number> | null;
  override_direction: string | null; regulation_ref: string | null;
  label: string; comment: string | null;
  status: string; version: number; is_disabled: boolean;
  created_at: string | null; updated_at: string | null;
}
export interface ConflictPolicyUpdateInput {
  expected_version: number; strategy?: string | null;
  priority_lattice?: Record<string, number> | null;
  override_direction?: string | null; regulation_ref?: string | null;
  comment?: string | null; is_disabled?: boolean | null;
}
export const listConflictPolicies = () =>
  fetchAPI<TBoxConflictPolicy[]>("/api/ontology/conflict-policies");
export const updateConflictPolicy = (dimension: string, data: ConflictPolicyUpdateInput) =>
  fetchAPI<TBoxConflictPolicy>(`/api/ontology/conflict-policies/${encodeURIComponent(dimension)}`, {
    method: "PUT", ...jsonBody(data),
  });
export const publishConflictPolicy = (dimension: string, expectedVersion: number) =>
  fetchAPI<TBoxConflictPolicy>(
    `/api/ontology/conflict-policies/${encodeURIComponent(dimension)}/publish`,
    { method: "POST", ...jsonBody({ expected_version: expectedVersion }) },
  );

// ===========================================================================
// 文档标注 + 自动抽取 + 系统配置 + 全类列表 (UI 改进)
// ===========================================================================

export interface PropertyTriple {
  iri: string;
  label: string;
  value: string;
}

export interface EntityTriple {
  entity_text: string;
  entity_class_iri: string;
  entity_class_label: string;
  segment_index: number;
  span_start: number;
  span_end: number;
  properties: PropertyTriple[];
}

/** 右侧「关联信息」面板的识别实体（按 类+文本 去重、累计出现次数）。 */
export interface RecognizedEntity {
  text: string;
  classIri: string;
  classLabel: string;
  count: number;
}

/** 标注三元组 → 去重后的识别实体列表（同一 类+文本 合并、累计次数），供关联信息面板渲染。 */
export function entitiesFromTriples(triples: EntityTriple[]): RecognizedEntity[] {
  const byKey = new Map<string, RecognizedEntity>();
  for (const t of triples) {
    const text = (t.entity_text || "").trim();
    if (!text) continue;
    const key = `${t.entity_class_iri}::${text}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      byKey.set(key, {
        text,
        classIri: t.entity_class_iri,
        classLabel: t.entity_class_label || (t.entity_class_iri.split("#").flatMap((part) => part.split("/")).pop() ?? t.entity_class_iri),
        count: 1,
      });
    }
  }
  return Array.from(byKey.values());
}

// 文档级分类 + 全量关系/属性抽取（仅 Word；规则式、离线）。
export interface DocClassification {
  doc_class_iri: string;
  label: string;
  score: number;
  signals: string[];
  source?: string;
}

// 关系边上对象端点回填的数据属性；``iri`` 为 null 表示未匹配到本体数据属性（原文兜底）。
export interface RelationDataProperty {
  source?: FinderSource;
  iri: string | null;
  label: string;
  value: unknown;
}

/**
 * Document Profile locators preserve structural coordinates so the frontend can
 * navigate to the exact section, paragraph, table row, or table cell.
 */
export interface StructuredRelationSourceRef {
  kind?: string;
  section?: string;
  heading_index?: number | null;
  paragraph_index?: number | null;
  table?: number;
  row?: number;
  column?: number | null;
  header?: string;
  key?: string;
  parameter?: string;
  system?: string;
  entity?: string;
  record?: unknown;
  location?: unknown;
  [key: string]: unknown;
}

export type RelationSourceRef = string | StructuredRelationSourceRef;

// CMCReport 共线评估端点上的「推导 PDE vs 原文 PDE」冲突（确定性推导管线产出，供人工裁决）。
export interface PdeConflict {
  conflict_key: string;
  asserted: { pde_mg_day: number; pde_ug_day: number; band: number };
  derived: {
    band: number;
    band_point: number | null;
    pde_ug_day: number | null;
    oel_ug_m3: number | null;
    provisional: boolean;
    input_source: string;
    // 可复现审计记录（method/formula/inputs/factors/intermediate/bands/provisional）。
    provenance: Record<string, unknown>;
  };
  delta_bands: number;
  pde_ratio?: number;
  summary: string;
}

// 子关系（如 合成路线→包含步骤→使用设备/产出中间体），``sub_relationships`` 递归。
export interface SubRelationship {
  source?: FinderSource;
  predicate_iri: string;
  predicate_label: string;
  object_class_iri: string;
  object_class_label: string;
  object_text: string;
  object_source: string;
  object_data_properties: RelationDataProperty[];
  sub_relationships: SubRelationship[];
  source_ref: RelationSourceRef | null;
  // 仅 CMCReport 共线评估端点可能携带；命中「推导 vs 原文」PDE 冲突时下发（人工裁决）。
  conflict?: PdeConflict | null;
  pde_review_issues?: string[];
}

// 顶层对象属性边（主语为文档分类类，如 CMCReport ─describes→ DrugProduct）。
export interface Relationship extends SubRelationship {
  subject_class_iri: string;
  subject_class_label: string;
  subject_text: string;
}

export type SummaryStatus =
  | "pending"
  | "completed"
  | "partial"
  | "disabled"
  | "failed";
export type SummarySource = "llm" | "extractive_fallback" | "empty" | "none";

export interface WordSourceRange {
  anchor_block_id: string | null;
  start_block_id: string | null;
  end_block_id: string | null;
  heading_index: number | null;
}

export interface WordLayerMetadata {
  content_summary: string | null;
  summary_scope: "subtree";
  summary_status: SummaryStatus;
  summary_source: SummarySource;
  summary_model: string | null;
  prompt_version: string;
  content_hash: string;
  generated_at: string | null;
  direct_paragraph_count: number;
  direct_table_count: number;
  descendant_section_count: number;
  leaf_count: number;
  page_count: number;
}

export interface WordPageMetadata {
  content_summary: string | null;
  summary_scope: "page_segment";
  summary_status: Exclude<SummaryStatus, "partial">;
  summary_source: SummarySource;
  summary_model: string | null;
  prompt_version: string;
  content_hash: string;
  generated_at: string | null;
  paragraph_count: number;
  table_count: number;
  character_count: number;
}

export interface WordPageNode {
  node_id: string;
  node_type: "page";
  ordinal_in_leaf: number;
  physical_page_number: number | null;
  break_source: string | null;
  block_ids: string[];
  paragraph_indices: number[];
  table_indices: number[];
  source_range: WordSourceRange;
  page_metadata: WordPageMetadata;
}

export interface WordChapterNode {
  node_id: string;
  node_type: "document" | "section";
  heading: string;
  level: number;
  path: string[];
  heading_index: number | null;
  is_leaf: boolean;
  direct_block_ids: string[];
  paragraph_indices: number[];
  table_indices: number[];
  source_range: WordSourceRange;
  layer_metadata: WordLayerMetadata;
  pages: WordPageNode[];
  children: WordChapterNode[];
}

export interface WordPaginationMetadata {
  mode: "rendered_markers" | "explicit_markers" | "single_page_fallback";
  physical_page_numbers_available: boolean;
  is_estimated: boolean;
  warning: string | null;
}

export interface AnnotatedDocument {
  source_type: string;
  filename: string | null;
  content: unknown;
  warnings?: string[];
  triples?: EntityTriple[];
  doc_class?: DocClassification | null;
  relationships?: Relationship[];
  section_tree?: WordChapterNode;
  pagination?: WordPaginationMetadata;
  preview_only?: boolean;
  completion?: "complete" | "incomplete";
  evidence_run?: {
    completion: "complete" | "incomplete";
    degraded: boolean;
    diagnostics: string[];
  };
}
export const getAnnotatedDocument = (jobId: string, refresh = false, signal?: AbortSignal) =>
  fetchAPI<AnnotatedDocument>(
    `/api/extraction/jobs/${jobId}/annotated-document${refresh ? "?refresh=1" : ""}`,
    { signal },
  );

// Persistent ontology-guided document recognition. This is the only browser
// contract for Word analysis; GETs below are read-only snapshot retrievals.
export const DOCUMENT_ANALYSIS_CONTRACT_VERSION = "document-analysis-runs-v1" as const;

export type DocumentAnalysisMetadataMode =
  | "cached_summary"
  | "generate_summary"
  | "structure_only";
export type DocumentAnalysisAvailability = "pending" | "ready" | "partial" | "failed";
export type DocumentAnalysisStatus =
  | "queued"
  | "running"
  | "paused"
  | "finished"
  | "retryable_failure"
  | "blocked_dependency"
  | "cancelled"
  | "deleting"
  | "deleted"
  | "expired";
export type DocumentAnalysisAvailableAction =
  | "pause" | "resume" | "cancel" | "delete"
  | "ranking_budget_enable" | "ranking_budget_disable";

export type DocumentAnalysisStage =
  | "accepted"
  | "storing_source"
  | "converting"
  | "parsing"
  | "preparing_metadata"
  | "freezing_inputs"
  | "planning"
  | "extracting"
  | "projecting"
  | "finalizing"
  | "complete";

export interface DocumentAnalysisCreateInput {
  filename: string;
  root_class_iri: string;
  root_class_label: string;
  metadata_mode: DocumentAnalysisMetadataMode;
}

export interface DocumentAnalysisRunInput extends DocumentAnalysisCreateInput {
  scope_mode: "document_graph" | "focus_path";
}

export interface DocumentAnalysisRunLinks {
  self: string;
  metadata: string | null;
  graph: string;
  source: string;
  events: string;
}

export interface DocumentAnalysisCreateResponse {
  contract_version: typeof DOCUMENT_ANALYSIS_CONTRACT_VERSION;
  recognition_run_id: string;
  run_revision: number;
  event_head: number;
  artifact_revision: number;
  status: DocumentAnalysisStatus;
  stage: DocumentAnalysisStage;
  idempotent_replay: boolean;
  input: DocumentAnalysisCreateInput;
  created_at: string;
  expires_at: string | null;
  links: DocumentAnalysisRunLinks;
}

export type DocumentAnalysisRunSummary = Pick<
  DocumentAnalysisCreateResponse,
  | "contract_version" | "recognition_run_id" | "run_revision" | "event_head"
  | "artifact_revision" | "status" | "stage" | "input" | "created_at" | "expires_at"
>;

export interface DocumentAnalysisRunList {
  contract_version: typeof DOCUMENT_ANALYSIS_CONTRACT_VERSION;
  items: DocumentAnalysisRunSummary[];
  has_more: boolean;
}

export interface DocumentAnalysisDecisionCounts {
  supported: number;
  unsupported: number;
  undetermined: number;
  not_checked: number;
}

export interface DocumentRetrievalDiagnostics {
  schema_version: 1;
  policy_version: "adaptive-retrieval-v1";
  records_soft_pruned: number;
  records_pending_disposition: number;
  records_reactivatable: number;
  records_dependency_exhausted: number;
  reason_counts: Record<string, number>;
  search_status: string;
  pruning_quality?: "unvalidated";
}

export interface DocumentRecordDiscoveryDiagnostics {
  policy: "semantic-record-discovery-v1";
  mode: "semantic" | "deterministic";
  reading_groups: number;
  ranked_groups: number;
  remaining_pairs: number;
  admitted_pairs: number;
  unselected_pairs: number;
  unselected_groups: number;
  routing_cards?: number;
  metadata_nodes?: number;
  routed_groups?: number;
  unrouted_groups?: number;
  selected_regions?: number;
  execution_mode?: "per_card" | "region_batch";
  property_field_mode?: "separate" | "region_batch";
}

export interface DocumentAnalysisProgress {
  candidate_policy?: "sparse-candidates-v1" | null;
  completion?: "incomplete" | "policy_complete";
  retrieval_diagnostics?: DocumentRetrievalDiagnostics;
  record_discovery?: DocumentRecordDiscoveryDiagnostics;
  tasks_attempted: number;
  model_calls: number;
  model_calls_reserved?: number;
  model_calls_unresolved?: number;
  records_planned: number;
  records_examined: number;
  records_incomplete: number;
  records_unattempted: number;
  phase_counts: { phase1: number; phase2: number };
  decisions: DocumentAnalysisDecisionCounts;
  pending_frontiers: number;
  stop_reason: string | null;
  contract_version: typeof DOCUMENT_ANALYSIS_CONTRACT_VERSION;
  event_head: number;
  artifact_revision: number;
}

export interface DocumentAnalysisRunIdentities {
  analysis_id: string | null;
  structure_snapshot_id?: string | null;
  ranking_summary_id?: string | null;
  ontology_snapshot_id: string | null;
  metadata_snapshot_id: string | null;
  graph_snapshot_id: string | null;
  fingerprint_status: "provisional" | "frozen";
}

export interface DocumentAnalysisRun {
  extraction_protocol?: string | null;
  contract_version: typeof DOCUMENT_ANALYSIS_CONTRACT_VERSION;
  recognition_run_id: string;
  run_revision: number;
  event_head: number;
  artifact_revision: number;
  status: DocumentAnalysisStatus;
  stage: DocumentAnalysisStage;
  input: DocumentAnalysisRunInput;
  identities: DocumentAnalysisRunIdentities;
  artifacts: {
    source: DocumentAnalysisAvailability;
    structure: DocumentAnalysisAvailability;
    metadata: DocumentAnalysisAvailability;
    graph: DocumentAnalysisAvailability;
  };
  progress: DocumentAnalysisProgress;
  error: DocumentAnalysisRunFailure | null;
  available_actions: DocumentAnalysisAvailableAction[];
  ranking_budget_enabled: boolean;
  created_at: string;
  started_at: string | null;
  paused_at: string | null;
  finished_at: string | null;
  expires_at: string | null;
}

export interface DocumentAnalysisRunFailure {
  code: string;
  stage: DocumentAnalysisStage;
  retryable: boolean;
  safe_detail: string;
  occurred_at: string;
}

export interface DocumentAnalysisMetadataArtifact {
  contract_version: typeof DOCUMENT_ANALYSIS_CONTRACT_VERSION;
  recognition_run_id: string;
  run_revision: number;
  event_head: number;
  artifact_revision: number;
  availability: DocumentAnalysisAvailability;
  stage: DocumentAnalysisStage;
  retry_after_ms?: number | null;
  analysis: {
    analysis_id: string;
    document_hash: string;
    structure_hash: string;
    parser_version: string;
    structure_policy_version: string;
  } | null;
  metadata_snapshot: {
    snapshot_id: string;
    generation_source: "model_summary" | "extractive_fallback" | "structure_only" | "mixed";
    summary_version: string | null;
    summary_model_identity: string | null;
    dependency_hash: string;
    frozen: boolean;
  } | null;
  metadata: null;
  filename: string | null;
  content: Record<string, unknown> | null;
  section_tree: WordChapterNode | null;
  pagination: WordPaginationMetadata | null;
  warnings: string[];
  error: DocumentAnalysisRunFailure | null;
}

export interface DocumentAnalysisEntityRef {
  entity_id: string;
  revision: number;
}

export interface DocumentAnalysisObjectRef {
  id: string;
  revision: number;
}
export type DocumentAnalysisAssertionPolarity =
  | "affirmed"
  | "negated"
  | "conditional"
  | "uncertain";

export type DocumentGraphProjection =
  | "verified"
  | "effective_affirmed"
  | "all_candidates"
  | "unassociated"
  | "negated"
  | "conditional"
  | "undetermined"
  | "rejected";

export interface DocumentGraphSnapshotIdentity {
  snapshot_id: string;
  analysis_id: string;
  metadata_snapshot_id: string;
  ontology_snapshot_id: string;
  root_ref: DocumentAnalysisEntityRef;
  projection_policy: string;
  generated_at: string;
}

export interface ExternalRecordProvenance {
  kind: "external_record";
  system: string;
  dataset: string;
  record_key: string;
  record_version: string;
  field_path: string;
  value: unknown;
  fetched_at?: string | null;
  applicable_at?: string | null;
  identity_match_evidence: EvidenceAnchor[];
  record_snapshot: Record<string, unknown>;
}

export interface DocumentGraphEntity {
  grounding_kind?: "document_root" | "mention" | "record" | null;
  type_decision_ref?: DocumentAnalysisObjectRef | null;
  referent_decision_ref?: DocumentAnalysisObjectRef | null;
  composition_decision_ref?: DocumentAnalysisObjectRef | null;
  external_provenance?: ExternalRecordProvenance[];
  identity_decision_refs?: DocumentAnalysisObjectRef[];
  entity_id: string;
  revision: number;
  class_iri: string;
  class_label: string;
  label: string;
  seed_origin: "user_selected" | "recognized";
  identity_state: "document_local" | "verified_key" | "verified_external" | "undetermined";
  independent_review: "unreviewed" | "accepted" | "rejected";
  source_selection_refs: string[];
  predicate_menu?: Array<{
    predicate_iri: string;
    predicate_label: string;
    kind: "property" | "relationship";
  }> | null;
}

export type DocumentAssertionModality = "asserted" | "required" | "possible" | "planned" | "unspecified";
export type DocumentRelationSelection = "all" | "one_of" | "alternatives" | "undetermined";
export interface DocumentGraphScope {
  scope_id: string;
  members: Array<{ relation_ref: DocumentAnalysisObjectRef; member_ref: DocumentAnalysisEntityRef }>;
}
export interface DocumentGraphScopeResolution {
  scope_id: string;
  steps: Array<DocumentGraphScope["members"][number] & {
    selection: DocumentRelationSelection | null;
    polarity: DocumentAnalysisAssertionPolarity;
    modality: DocumentAssertionModality;
    conditions: string[];
    applicability: Array<{ predicate_iri: string | null; text: string; evidence_selection_ids: string[] }>;
    evidence_selection_ids: string[];
  }>;
}

export function defaultDocumentGraphProjection(run?: { extraction_protocol?: string | null } | null): DocumentGraphProjection {
  return run?.extraction_protocol === "ontology-tool-extraction-v1" ? "verified" : "effective_affirmed";
}

export interface DocumentGraphAssertionBase {
  decision_status: "supported" | "unsupported" | "undetermined" | "not_checked";
  validation_diagnostics: Array<{
    check: "metric" | "shacl" | "relation_graph" | "schema";
    status: "passed" | "failed" | "incomplete" | "not_checked";
    reason_codes: string[];
    message: string;
  }>;
  modality?: DocumentAssertionModality | null;
  scope?: DocumentGraphScope | null;
  candidate_id: string;
  revision: number;
  subject_ref: DocumentAnalysisEntityRef;
  predicate_iri: string;
  predicate_label: string;
  polarity: DocumentAnalysisAssertionPolarity;
  conditions: Array<Record<string, unknown>>;
  applicability: Record<string, unknown>;
  structural_valid: boolean;
  model_supported: boolean;
  policy_eligible: boolean;
  independent_review: "unreviewed" | "accepted" | "rejected";
  proof_ref: DocumentAnalysisObjectRef | null;
  decision_refs: DocumentAnalysisObjectRef[];
  dependency_refs: DocumentAnalysisObjectRef[];
  source_selection_refs: {
    selection?: string[];
    unit?: string[];
    subject: string[];
    object: string[];
    value: string[];
    predicate_bridge: string[];
    condition: string[];
    counterevidence: string[];
  };
  reason_code: string | null;
  reason: string | null;
  invalidated: boolean;
}

export interface DocumentGraphProperty extends DocumentGraphAssertionBase {
  direction: "subject_to_value";
  raw_value: string;
  raw_unit: string | null;
  normalization_available: boolean;
  normalized_value: unknown;
  datatype_iri: string | null;
  unit: string | null;
  normalization_record?: {
    raw_unit?: string;
    from?: string;
    to?: string;
    factor?: string;
    offset?: string;
    mapping_kind?: "identity" | "alias" | "conversion";
    registry_version?: string;
    policy_version?: string;
    datatype_iri?: string;
  };
}

export function formatDocumentGraphQuantity(
  item: Pick<DocumentGraphProperty, "normalized_value" | "unit">,
): string | null {
  const value = item.normalized_value;
  let text: string;
  if (value == null) return null;
  if (typeof value !== "object") {
    text = String(value);
  } else {
    const quantity = value as Record<string, unknown>;
    if (quantity.form === "scalar" && typeof quantity.scalar === "string") {
      text = quantity.scalar;
    } else if (quantity.form === "interval" && typeof quantity.lower === "string"
      && typeof quantity.upper === "string" && typeof quantity.lower_inclusive === "boolean"
      && typeof quantity.upper_inclusive === "boolean") {
      text = `${quantity.lower_inclusive ? "[" : "("}${quantity.lower}, ${quantity.upper}${quantity.upper_inclusive ? "]" : ")"}`;
    } else if (quantity.form === "lower_bound" && typeof quantity.lower === "string"
      && (quantity.comparator === "ge" || quantity.comparator === "gt")) {
      text = `${quantity.comparator === "ge" ? "≥" : ">"} ${quantity.lower}`;
    } else if (quantity.form === "upper_bound" && typeof quantity.upper === "string"
      && (quantity.comparator === "le" || quantity.comparator === "lt")) {
      text = `${quantity.comparator === "le" ? "≤" : "<"} ${quantity.upper}`;
    } else {
      return null;
    }
  }
  return item.unit ? `${text} ${item.unit}` : text;
}

export type DocumentPropertyReviewReason = "incorrect_value" | "incorrect_property"
  | "incorrect_subject" | "incorrect_scope" | "unsupported" | "other";
export interface DocumentPropertyReview {
  review_id: string;
  revision: number;
  candidate_id: string;
  candidate_revision: number;
  graph_snapshot_id: string;
  decision: "accepted" | "rejected";
  reason_code: DocumentPropertyReviewReason;
  reason: string;
  author: string;
  author_role: string;
  created_at: string;
}
export interface DocumentPropertyReviewList {
  run_revision: number;
  items: DocumentPropertyReview[];
  heads: DocumentPropertyReview[];
  can_review: boolean;
  can_repair: boolean;
}
export interface DocumentPropertyReviewInput {
  request_key: string;
  expected_run_revision: number;
  graph_snapshot_id: string;
  candidate_id: string;
  candidate_revision: number;
  expected_review_revision: number;
  decision: "accepted" | "rejected";
  reason_code: DocumentPropertyReviewReason;
  reason: string;
}
export interface DocumentPropertyRepair {
  operation_id: string;
  review_id: string;
  candidate_id: string;
  candidate_revision: number;
  status: "queued" | "running" | "completed" | "unresolved" | "failed" | "cancelled";
  subject_ref: DocumentAnalysisEntityRef;
  predicate_iri: string;
  record_ids: string[];
  max_tasks: 16;
  max_model_calls: 32;
  result: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}
export interface DocumentPropertyRepairList {
  run_revision: number;
  items: DocumentPropertyRepair[];
  can_repair: boolean;
}
export interface DocumentPropertyRepairInput {
  request_key: string;
  expected_run_revision: number;
  review_id: string;
}
export interface DocumentPropertyReviewReceipt {
  review: DocumentPropertyReview;
  run: DocumentAnalysisRun;
}
export interface DocumentPropertyRepairReceipt {
  operation: DocumentPropertyRepair;
  run: DocumentAnalysisRun;
}

export interface DocumentGraphRelationship extends DocumentGraphAssertionBase {
  object_ref: DocumentAnalysisEntityRef;
  direction: "subject_to_object" | "object_to_subject";
}

export interface DocumentGraphRelationshipGroup extends DocumentGraphAssertionBase {
  object_refs: DocumentAnalysisEntityRef[];
  direction: "subject_to_object" | "object_to_subject";
  selection: DocumentRelationSelection;
}

export interface DocumentGraphCoverageSubject {
  scope?: DocumentGraphScope | null;
  candidate_policy?: "sparse-candidates-v1" | null;
  retrieval_diagnostics?: DocumentRetrievalDiagnostics;
  subject_ref: DocumentAnalysisEntityRef;
  predicate_iri: string;
  predicate_label: string;
  records_planned: number;
  records_examined: number;
  records_incomplete: number;
  records_unattempted: number;
  phase_counts: { phase1: number; phase2: number };
  executed_phase_counts?: { phase1: number; phase2: number } | null;
  pending_frontiers: number;
  stop_reason: string | null;
}

export interface DocumentGraphCoverage {
  candidate_policy?: "sparse-candidates-v1" | null;
  retrieval_diagnostics?: DocumentRetrievalDiagnostics;
  record_discovery?: DocumentRecordDiscoveryDiagnostics;
  subjects: DocumentGraphCoverageSubject[];
  records_planned: number;
  records_examined: number;
  records_incomplete: number;
  records_unattempted: number;
  phase2_started: boolean;
  pending_frontiers: number;
  stop_reason: string | null;
}

export interface DocumentGraphRanking {
  budget_enabled: boolean;
  requested_mode: string;
  actual_modes: string[];
  degraded: boolean;
  paused?: boolean;
  reasons: string[];
  committed_epochs: number;
  cost: {
    model_calls: number;
    observed_requests?: number;
    input_pairs: number;
    input_tokens: number;
    reserved_input_tokens?: number;
    measured_input_tokens?: number;
    unknown_request_count?: number;
    queue_seconds?: number;
    retries: number;
    elapsed_seconds: number;
  };
  epochs: {
    budget_accounted: boolean;
    epoch_id: string;
    status?: "committed" | "paused";
    query_id: string | null;
    subject_ref: DocumentAnalysisEntityRef | null;
    predicate_iri: string | null;
    plan_id: string | null;
    requested_mode: string;
    actual_mode: string;
    degraded: boolean;
    reason: string | null;
    records: {
      record_id: string;
      rank: number;
      channels: string[];
      intent_ranks: Record<string, number>;
      raw_scores: Record<string, number>;
    }[];
  }[];
}

export interface DocumentParsedAttributeValue {
  value: string | boolean | null;
  datatype_iri: string | null;
  quantity: {
    kind: "number" | "range" | "comparison";
    operator?: string | null;
    scalar?: string | null;
    lower?: string | null;
    upper?: string | null;
    lower_inclusive?: boolean | null;
    upper_inclusive?: boolean | null;
    source_unit?: string | null;
    dimension?: string | null;
  } | null;
  precision: string | null;
  issues: string[];
}

export interface DocumentAttributeCandidate {
  candidate_id: string;
  record_id: string;
  field_id: string | null;
  field_label: string;
  raw_value: string;
  parsed_value: DocumentParsedAttributeValue | null;
  options: Array<{
    subject_ref: DocumentAnalysisObjectRef;
    subject_label: string | null;
    class_iri: string;
    predicate_iri: string;
    predicate_label: string | null;
  }>;
  status: "pending" | "rejected_mapping" | "resolved";
  reason_codes: string[];
  checks: Record<string, string>;
  source_claim_id: string | null;
  source_selection_refs: { label: string[]; value: string[] };
}

export interface DocumentAnalysisGraphArtifact {
  extraction_protocol?: string | null;
  relationship_groups?: DocumentGraphRelationshipGroup[];
  scope_resolutions?: DocumentGraphScopeResolution[];
  contract_version: typeof DOCUMENT_ANALYSIS_CONTRACT_VERSION;
  recognition_run_id: string;
  run_revision: number;
  event_head: number;
  artifact_revision: number;
  availability: DocumentAnalysisAvailability;
  projection: DocumentGraphProjection;
  graph_snapshot: DocumentGraphSnapshotIdentity | null;
  entities: DocumentGraphEntity[];
  properties: DocumentGraphProperty[];
  attribute_candidates: DocumentAttributeCandidate[];
  relationships: DocumentGraphRelationship[];
  invalidated_refs: DocumentAnalysisObjectRef[];
  coverage: DocumentGraphCoverage;
  ranking?: DocumentGraphRanking;
  evidence_repair?: {
    enabled: boolean;
    total: number;
    status_counts: Record<string, number>;
    reason_counts: Record<string, number>;
    rechecks: number;
  };
  unresolved: {
    unsupported: number;
    undetermined: number;
    not_checked: number;
    unassociated_entities: number;
  };
  error: DocumentAnalysisRunFailure | null;
}

export interface DocumentSourceSelection {
  selection_ref: string;
  section_node_id: string;
  source_record_ref: string | null;
  record_view_ref: string | null;
  source_cell_id: string | null;
  span_refs: string[];
  selection_role: "entity" | "subject" | "object" | "value" | "unit" | "predicate_bridge" | "condition" | "counterevidence" | "selection";
}

export interface DocumentAnalysisSourceArtifact {
  contract_version: typeof DOCUMENT_ANALYSIS_CONTRACT_VERSION;
  recognition_run_id: string;
  analysis_id: string;
  document_hash: string;
  structure_hash: string;
  filename: string;
  content: Record<string, unknown>;
  selection: DocumentSourceSelection | null;
  anchors: EvidenceAnchor[];
}

export type DocumentAnalysisSourceSelectionArtifact = Omit<
  DocumentAnalysisSourceArtifact, "filename" | "content"
>;

export interface DocumentAnalysisRunControl {
  contract_version: typeof DOCUMENT_ANALYSIS_CONTRACT_VERSION;
  recognition_run_id: string;
  run_revision: number;
  event_head: number;
  artifact_revision: number;
  status: DocumentAnalysisStatus;
  stage: DocumentAnalysisStage;
  operation: DocumentAnalysisAvailableAction;
  operation_status: string;
  available_actions: DocumentAnalysisAvailableAction[];
  ranking_budget_enabled: boolean;
}

export type DocumentAnalysisControlAction = Exclude<DocumentAnalysisAvailableAction, "delete">;

const DOCUMENT_ANALYSIS_EVENT_STREAM_POLICY: Readonly<Record<DocumentAnalysisStatus, boolean>> = {
  queued: true,
  running: true,
  deleting: true,
  paused: false,
  retryable_failure: false,
  blocked_dependency: false,
  finished: false,
  cancelled: false,
  deleted: false,
  expired: false,
};

function isDocumentAnalysisStatus(value: unknown): value is DocumentAnalysisStatus {
  return typeof value === "string"
    && Object.prototype.hasOwnProperty.call(DOCUMENT_ANALYSIS_EVENT_STREAM_POLICY, value);
}

/**
 * Only execution states that can still produce autonomous progress keep an
 * SSE subscription open. Paused/retryable runs are deliberately quiescent
 * until an explicit resume transitions the same run back to queued.
 */
export function shouldSubscribeDocumentAnalysisEvents(
  status: DocumentAnalysisStatus | null | undefined,
): boolean {
  return status != null && DOCUMENT_ANALYSIS_EVENT_STREAM_POLICY[status];
}

/** Apply the authoritative overlap in a control receipt to a loaded snapshot. */
export function mergeDocumentAnalysisControlReceipt(
  current: DocumentAnalysisRun | null,
  receipt: DocumentAnalysisRunControl,
): DocumentAnalysisRun | null {
  if (
    current == null
    || current.recognition_run_id !== receipt.recognition_run_id
    || receipt.run_revision < current.run_revision
    || receipt.event_head < current.event_head
    || receipt.artifact_revision < current.artifact_revision
  ) return current;
  return {
    ...current,
    run_revision: receipt.run_revision,
    event_head: receipt.event_head,
    artifact_revision: receipt.artifact_revision,
    status: receipt.status,
    stage: receipt.stage,
    available_actions: receipt.available_actions,
    ranking_budget_enabled: receipt.ranking_budget_enabled ?? current.ranking_budget_enabled ?? true,
  };
}

export type DocumentAnalysisEventType =
  | "run_state"
  | "progress"
  | "artifact"
  | "warning"
  | "error"
  | "heartbeat"
  | "tombstone";

export interface DocumentAnalysisRunEvent {
  contract_version: typeof DOCUMENT_ANALYSIS_CONTRACT_VERSION;
  event_id: string;
  recognition_run_id: string;
  run_revision: number;
  event_head: number;
  artifact_revision: number;
  status: DocumentAnalysisStatus;
  stage: DocumentAnalysisStage;
  artifact_kind: "source" | "structure" | "metadata" | "graph" | null;
  availability: DocumentAnalysisAvailability | null;
}

const documentRunPath = (recognitionRunId: string) =>
  `/api/document-analysis/runs/${encodeURIComponent(recognitionRunId)}`;

const templateRunPath = (templateId: string, jobId: string) =>
  `/api/document-analysis/templates/${encodeURIComponent(templateId)}/sources/${encodeURIComponent(jobId)}/runs`;

export const getTemplateDocumentRun = (templateId: string, jobId: string, signal?: AbortSignal) =>
  fetchAPI<{ run: DocumentAnalysisRun | null }>(templateRunPath(templateId, jobId), { signal });

export const createTemplateDocumentRun = (
  templateId: string, jobId: string, requestKey: string, signal?: AbortSignal,
) => fetchAPI<DocumentAnalysisCreateResponse>(templateRunPath(templateId, jobId), {
  method: "POST", body: JSON.stringify({ request_key: requestKey }), signal,
});

export interface ReportDocumentSource {
  document_iri: string;
  source_job_id: string;
  document_version: string;
  root_class_iri: string;
  filename: string;
  document_hash: string;
  analysis_id: string;
  structure_hash: string;
  content: Record<string, unknown>;
  section_tree: WordChapterNode;
  pagination: WordPaginationMetadata;
  warnings: string[];
}

const reportDocumentPath = (resource: "source" | "runs", documentIri: string) =>
  `/api/document-analysis/documents/${resource}?${new URLSearchParams({ document_iri: documentIri })}`;

export const getReportDocumentSource = (documentIri: string, signal?: AbortSignal) =>
  fetchAPI<ReportDocumentSource>(reportDocumentPath("source", documentIri), { signal, cache: "no-store" });

export const getReportDocumentRun = (documentIri: string, signal?: AbortSignal) =>
  fetchAPI<{ run: DocumentAnalysisRun | null }>(reportDocumentPath("runs", documentIri), {
    signal, cache: "no-store",
  });

export const createReportDocumentRun = (
  documentIri: string, requestKey: string, signal?: AbortSignal, templateId?: string,
) => fetchAPI<DocumentAnalysisCreateResponse>(reportDocumentPath("runs", documentIri)
  + (templateId ? `&template_id=${encodeURIComponent(templateId)}` : ""), {
  method: "POST", body: JSON.stringify({ request_key: requestKey }), signal,
});

export const createDocumentAnalysisRun = (
  file: File,
  rootClassIri: string,
  requestKey: string,
  metadataMode: DocumentAnalysisMetadataMode = "generate_summary",
  signal?: AbortSignal,
) => {
  const form = new FormData();
  form.append("file", file);
  form.append("root_class_iri", rootClassIri);
  form.append("request_key", requestKey);
  form.append("metadata_mode", metadataMode);
  return fetchAPI<DocumentAnalysisCreateResponse>("/api/document-analysis/runs", {
    method: "POST",
    body: form,
    signal,
  });
};

export const listDocumentAnalysisRuns = (limit = 20, offset = 0, signal?: AbortSignal) => {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  return fetchAPI<DocumentAnalysisRunList>(`/api/document-analysis/runs?${params}`, {
    signal,
    cache: "no-store",
  });
};

export const getDocumentAnalysisRun = (recognitionRunId: string, signal?: AbortSignal) =>
  fetchAPI<DocumentAnalysisRun>(documentRunPath(recognitionRunId), { signal }).then((run) => ({
    ...run,
    ranking_budget_enabled: run.ranking_budget_enabled ?? true,
  }));

export const getDocumentAnalysisMetadata = (recognitionRunId: string, signal?: AbortSignal) =>
  fetchAPI<DocumentAnalysisMetadataArtifact>(`${documentRunPath(recognitionRunId)}/metadata`, { signal });

export const getDocumentAnalysisGraph = (
  recognitionRunId: string,
  projection: DocumentGraphProjection = "effective_affirmed",
  signal?: AbortSignal,
) => fetchAPI<DocumentAnalysisGraphArtifact>(
  `${documentRunPath(recognitionRunId)}/graph?projection=${encodeURIComponent(projection)}`,
  { signal },
);

export interface DocumentAnalysisTarget {
  target_id: string;
  subject_ref: DocumentAnalysisEntityRef;
  subject_label: string;
  subject_class_iri: string;
  kind: "relationship" | "property";
  predicate_iri: string;
  predicate_label: string;
  range_types: Array<{ iri: string; label: string }>;
  datatype_iris: string[];
  multiplicity: "single" | "multiple" | "unspecified";
  state: "pending" | "partial" | "supported" | "not_found" | "undetermined" | "rejected" | "negated";
  supported_count: number;
  negated_count: number;
  completed: boolean;
  assertion_refs: DocumentAnalysisObjectRef[];
  supported_assertion_refs: DocumentAnalysisObjectRef[];
  coverage: {
    records_planned: number;
    records_examined: number;
    records_incomplete: number;
    records_unattempted: number;
    pending_frontiers: number;
    scope_checked: boolean;
  };
  scope_id: string | null;
  reason: string;
}

export interface DocumentAnalysisTargetGraph extends Pick<
  DocumentAnalysisRun, "contract_version" | "recognition_run_id" | "run_revision" | "event_head" | "artifact_revision"
> {
  phase: "candidate_graph" | "evidence_review" | "evidence_verification";
  availability: DocumentAnalysisAvailability;
  ontology_snapshot_id: string | null;
  root: DocumentAnalysisEntityRef & { class_iri: string; label: string };
  graph: DocumentAnalysisGraphArtifact;
  targets: DocumentAnalysisTarget[];
  discovery: {
    completed_calls: number;
    inflight_calls: number;
    candidate_count: number;
    items: Array<{
      id: string;
      kind: "entity" | "property" | "relation" | "observation" | "failure";
      label: string;
      class_iri: string | null;
      predicate_iri: string | null;
      subject_id: string | null;
      object_ids: string[];
      state: "pending" | "rejected" | "accepted" | "observation" | "failed";
      reasons: string[];
      sources: Array<{ text: string; selection_ref: string | null }>;
    }>;
  };
  summary: {
    relationships: { total: number; supported: number; completed: number; percent: number | null };
    properties: { total: number; supported: number; completed: number; percent: number | null };
    pending_expansion_count: number;
    notes: string[];
  };
}

export const getDocumentAnalysisTargetGraph = (runId: string, signal?: AbortSignal) =>
  fetchAPI<DocumentAnalysisTargetGraph>(`${documentRunPath(runId)}/target-graph`, {
    signal, cache: "no-store",
  });

export const DOCUMENT_HARNESS_PROTOCOL = "document-harness-v2" as const;
export type DocumentHarnessState = "candidate" | "accepted" | "rejected" | "unresolved";
export type DocumentHarnessStage = "ingest" | "parse" | "discover" | "type_alignment"
  | "referent_alignment" | "referent_candidates" | "referent_selection" | "entity_review"
  | "planning" | "property_alignment" | "relation_alignment" | "group_interpretation"
  | "evidence_review" | "coreference_review" | "identifier_check"
  | "literal_normalization" | "shacl_check" | "complete";
export type DocumentHarnessWorkStatus = "ready" | "waiting" | "pruned" | "done" | "failed";

export type DocumentHarnessPhase = "discovery" | "skeleton" | "semantic" | "deterministic" | "done";
export type DocumentHarnessCheckStatus = "not_run" | "passed" | "invalid" | "incomplete" | "not_applicable" | "error";
export interface DocumentHarnessCalibration {
  input_hash: string;
  checks: Record<"identifier" | "datatype" | "unit" | "shacl", {
    status: DocumentHarnessCheckStatus; reason_code: string | null; details: Record<string, unknown>;
  }>;
  literal: {
    kind: string; raw_value: string; normalized_value: string | boolean | null;
    lower: string | null; upper: string | null; operator: string;
    lower_inclusive: boolean; upper_inclusive: boolean;
    canonical_unit: string | null; conversion_record: Record<string, unknown>;
  } | null;
}
export interface DocumentHarnessVerification {
  method: "rule" | "llm" | null;
  rule_id: string | null;
  rule_version: string | null;
  semantic_verdict: "accepted" | "rejected" | "unresolved" | null;
}

export interface DocumentHarnessSourceRef {
  source_id: string;
  text: string;
  start: number;
  end: number;
  page: number | null;
  section_id: string;
  block_id: string;
}

export interface DocumentHarnessSource {
  source_id: string;
  text: string;
  page: number | null;
  section_id: string;
  block_id: string;
  row: number | null;
  column: number | null;
}

export interface DocumentHarnessMention {
  id: string;
  label: string;
  role: string;
  parent_mention_id: string | null;
  refined_member_ids: string[];
  verification: DocumentHarnessVerification;
  calibration: DocumentHarnessCalibration | null;
  class_iri: string | null;
  class_label: string | null;
  state: DocumentHarnessState;
  reason: string;
  evidence: DocumentHarnessSourceRef[];
}

export interface DocumentHarnessEntity extends DocumentHarnessMention {
  mentions: DocumentHarnessMention[];
}

export interface DocumentHarnessCoreference {
  id: string;
  left_mention_id: string;
  right_mention_id: string;
  verdict: "same" | "different" | "unresolved";
  basis: "explicit_alias" | "scoped_identifier" | "explicit_reference" | "distinct" | "insufficient";
  reason: string;
  evidence: DocumentHarnessSourceRef[];
  proof: DocumentHarnessSourceRef[];
  applied: boolean;
}

export interface DocumentHarnessCardRef {
  iri: string;
  label: string;
}

export interface DocumentHarnessPredicateRef extends DocumentHarnessCardRef {
  namespace: string;
  domain_text: string;
}

export interface DocumentHarnessObservation {
  id: string;
  kind: "field" | "entity" | "relation" | "scope" | "validation" | "failure";
  label: string;
  reason: string;
  evidence: DocumentHarnessSourceRef[];
  field_id: string | null;
  value: string | null;
  candidate_subject_ids: string[];
  object_id: string | null;
  discovery_cards: Array<DocumentHarnessCardRef & { role: "reading" | "document_properties" }>;
  alignments: Array<{
    subject_id: string;
    card: DocumentHarnessCardRef | null;
    property_ids: string[];
    attempts: Array<{
      state: "mapped" | "unmatched" | "invalid";
      reason: string;
      predicates: DocumentHarnessPredicateRef[];
    }>;
  }>;
}

export interface DocumentHarnessProperty {
  id: string;
  field_id: string | null;
  subject_id: string;
  subject_mention_id: string;
  card: DocumentHarnessCardRef | null;
  predicate: DocumentHarnessPredicateRef | null;
  predicate_iri: string | null;
  label: string;
  value: unknown;
  source_value: string;
  source_unit: string | null;
  source_unit_evidence: DocumentHarnessSourceRef[];
  value_component: "whole" | "span" | "lower" | "upper";
  value_evidence: DocumentHarnessSourceRef[];
  state: DocumentHarnessState;
  reason: string;
  evidence: DocumentHarnessSourceRef[];
  verification: DocumentHarnessVerification;
  calibration: DocumentHarnessCalibration | null;
}

export interface DocumentHarnessRelation {
  id: string;
  card: DocumentHarnessCardRef | null;
  predicate: DocumentHarnessPredicateRef | null;
  subject_id: string;
  object_id: string;
  subject_mention_id: string;
  object_mention_id: string;
  predicate_iri: string | null;
  label: string;
  state: DocumentHarnessState;
  reason: string;
  evidence: DocumentHarnessSourceRef[];
  polarity: "positive" | "negative" | "uncertain";
  conditions: string[];
  verification: DocumentHarnessVerification;
  calibration: DocumentHarnessCalibration | null;
}

export interface DocumentHarnessRelationGroup extends Omit<DocumentHarnessRelation, "object_id" | "object_mention_id"> {
  object_ids: string[];
  object_mention_ids: string[];
  participation: "options" | "all" | "unknown";
  selection: "exactly_one" | "unspecified";
  timing: "parallel" | "sequential" | "unspecified";
  timing_state: DocumentHarnessState;
  timing_reason: string;
  ordered_object_ids: string[] | null;
  order_evidence: DocumentHarnessSourceRef[];
}

export type DocumentInterpretationMeaning = "alternatives" | "parallel" | "joint_unspecified" | "unresolved";
export type DocumentInterpretationScope = "occurrence" | "document" | "platform";

export interface DocumentInterpretationTask {
  id: string;
  subject_label: string;
  object_labels: string[];
  relation_label: string;
  evidence: DocumentHarnessSourceRef[];
  questions: Array<{ prompt: string; options: Array<{ value: string; label: string }> }>;
  answer: { meaning: DocumentInterpretationMeaning; scope: DocumentInterpretationScope; revision: number } | null;
  scope_revisions: Record<DocumentInterpretationScope, number>;
}

export interface DocumentHarnessTarget {
  id: string;
  subject_id: string;
  predicate_iri: string;
  label: string;
  kind: "relation" | "property";
  range_labels: string[];
  state: "pending" | "candidate" | "accepted";
}

export interface DocumentHarnessGraph {
  protocol: typeof DOCUMENT_HARNESS_PROTOCOL;
  run_id: string;
  revision: number;
  status: string;
  stage: DocumentHarnessStage;
  progress: {
    completed_calls: number;
    candidate_count: number;
    fact_count: number;
    phase: DocumentHarnessPhase;
    phase_work_counts: Record<Exclude<DocumentHarnessPhase, "done">, Record<DocumentHarnessWorkStatus, number>>;
    calibration_counts: Record<"identifier" | "datatype" | "unit" | "shacl", Record<DocumentHarnessCheckStatus, number>>;
    reading_windows: {
      total: number;
      saved: number;
      complete: number;
      incomplete: number;
      active: number;
    };
    scope_complete: boolean;
    reading: {
      total_characters: number;
      processed_characters: number;
      complete_characters: number;
      complete: boolean;
    };
    work_counts: Record<DocumentHarnessWorkStatus, number>;
    candidate_scope_limited: boolean;
    rule_verified_count: number;
    llm_verified_count: number;
    stage_costs: Array<{
      phase: DocumentHarnessPhase;
      stage: DocumentHarnessStage;
      calls: number;
      seconds: number;
      input_tokens: number | null;
      output_tokens: number | null;
      unmeasured_attempts: number;
    }>;
  };
  entities: DocumentHarnessEntity[];
  coreferences: DocumentHarnessCoreference[];
  properties: DocumentHarnessProperty[];
  relations: DocumentHarnessRelation[];
  relation_groups: DocumentHarnessRelationGroup[];
  interpretation_tasks: DocumentInterpretationTask[];
  observations: DocumentHarnessObservation[];
  targets: DocumentHarnessTarget[];
  candidate_work: DocumentHarnessCandidateWork[];
}

export interface DocumentHarnessCandidateWork {
  id: string;
  kind: "relation_alignment" | "coreference_review";
  subject_id: string;
  object_ids: string[];
  predicate_iri: string | null;
  status: DocumentHarnessWorkStatus;
  reason_code: string | null;
  evidence: DocumentHarnessSourceRef[];
}

export const getDocumentHarnessGraph = (runId: string, signal?: AbortSignal) =>
  fetchAPI<DocumentHarnessGraph>(`${documentRunPath(runId)}/harness-graph`, {
    signal, cache: "no-store",
  });

export const getDocumentHarnessSource = (runId: string, sourceId: string, signal?: AbortSignal) =>
  fetchAPI<DocumentHarnessSource>(`${documentRunPath(runId)}/harness-source/${encodeURIComponent(sourceId)}`, {
    signal, cache: "no-store",
  });

export const answerDocumentInterpretationTask = (
  runId: string, taskId: string, input: {
    meaning: DocumentInterpretationMeaning;
    scope: DocumentInterpretationScope;
    expected_revision: number;
  }, signal?: AbortSignal,
) => fetchAPI<DocumentInterpretationTask>(
  `${documentRunPath(runId)}/interpretation-tasks/${encodeURIComponent(taskId)}/answer`,
  { method: "POST", ...jsonBody(input), signal },
);

export const getDocumentPropertyReviews = (runId: string, signal?: AbortSignal) =>
  fetchAPI<DocumentPropertyReviewList>(`${documentRunPath(runId)}/reviews`, { signal });
export const reviewDocumentProperty = (runId: string, input: DocumentPropertyReviewInput) =>
  fetchAPI<DocumentPropertyReviewReceipt>(`${documentRunPath(runId)}/reviews`, {
    method: "POST", ...jsonBody(input),
  });
export const getDocumentPropertyRepairs = (runId: string, signal?: AbortSignal) =>
  fetchAPI<DocumentPropertyRepairList>(`${documentRunPath(runId)}/repairs`, { signal });
export const repairDocumentProperty = (runId: string, input: DocumentPropertyRepairInput) =>
  fetchAPI<DocumentPropertyRepairReceipt>(`${documentRunPath(runId)}/repairs`, {
    method: "POST", ...jsonBody(input),
  });

export const getDocumentAnalysisSource = (
  recognitionRunId: string,
  selectionRef?: string,
  signal?: AbortSignal,
) => {
  const query = selectionRef ? `?selection_ref=${encodeURIComponent(selectionRef)}` : "";
  return fetchAPI<DocumentAnalysisSourceArtifact>(`${documentRunPath(recognitionRunId)}/source${query}`, { signal });
};

export const getDocumentAnalysisSourceSelection = (
  recognitionRunId: string, selectionRef: string, signal?: AbortSignal,
) => fetchAPI<DocumentAnalysisSourceSelectionArtifact>(
  `${documentRunPath(recognitionRunId)}/source-selection?selection_ref=${encodeURIComponent(selectionRef)}`,
  { signal },
);

export const getDocumentAnalysisRankingSummary = (
  recognitionRunId: string, signal?: AbortSignal,
  expected?: { summaryId: string; budgetEnabled: boolean },
) => {
  const query = expected ? `?${new URLSearchParams({
    expected_summary_id: expected.summaryId,
    expected_budget_enabled: String(expected.budgetEnabled),
  })}` : "";
  return fetchAPI<DocumentGraphRanking>(`${documentRunPath(recognitionRunId)}/ranking-summary${query}`, { signal });
};

/**
 * Subscribe to durable run events. Native EventSource reconnects with the last
 * numeric SSE id; each event is still checked against the requested run before
 * it can trigger a snapshot refresh.
 */
export function subscribeDocumentAnalysisEvents(
  recognitionRunId: string,
  onEvent: (event: DocumentAnalysisRunEvent, eventType: DocumentAnalysisEventType) => void,
  onConnectionChange?: (connected: boolean) => void,
  onHarness?: (snapshot: HarnessSnapshot) => void,
): () => void {
  const identity = getIdentity();
  const query = new URLSearchParams({
    x_user: identity.username,
    x_role: identity.role,
  });
  const token = getToken();
  if (token) query.set("token", token);
  const source = new EventSource(
    `${API_BASE}${documentRunPath(recognitionRunId)}/events?${query.toString()}`,
  );
  const eventTypes: DocumentAnalysisEventType[] = [
    "run_state",
    "progress",
    "artifact",
    "warning",
    "error",
    "heartbeat",
    "tombstone",
  ];
  const listeners = new Map<DocumentAnalysisEventType, EventListener>();
  const harnessListener: EventListener = (raw) => {
    try {
      const value = JSON.parse((raw as MessageEvent<string>).data);
      const snapshot = value.snapshot;
      if (value.recognition_run_id === recognitionRunId && snapshot
        && typeof snapshot.session_id === "string" && Number.isSafeInteger(snapshot.sequence)
        && typeof snapshot.output === "string" && typeof snapshot.thinking === "string"
        && Array.isArray(snapshot.operations)) onHarness?.(snapshot as HarnessSnapshot);
    } catch { /* Snapshot polling remains available after a malformed frame. */ }
  };
  source.addEventListener("harness", harnessListener);
  const opened: EventListener = () => onConnectionChange?.(true);
  const disconnected: EventListener = (event) => {
    // The server also emits named "error" frames; those are still live SSE.
    if (!("data" in event)) onConnectionChange?.(false);
  };
  source.addEventListener("open", opened);
  source.addEventListener("error", disconnected);
  let closed = false;
  const close = () => {
    if (closed) return;
    closed = true;
    for (const [eventType, listener] of listeners) {
      source.removeEventListener(eventType, listener);
    }
    source.removeEventListener("harness", harnessListener);
    source.removeEventListener("open", opened);
    source.removeEventListener("error", disconnected);
    source.close();
  };
  for (const eventType of eventTypes) {
    const listener: EventListener = (rawEvent) => {
      const data = (rawEvent as MessageEvent<string>).data;
      let event: DocumentAnalysisRunEvent;
      try {
        event = JSON.parse(data) as DocumentAnalysisRunEvent;
      } catch {
        // A malformed frame has no authority to mutate local run state. The
        // read-only snapshot poll remains the recovery path.
        return;
      }
      if (
        event.contract_version === DOCUMENT_ANALYSIS_CONTRACT_VERSION
        && event.recognition_run_id === recognitionRunId
        && isDocumentAnalysisStatus(event.status)
      ) {
        try {
          onEvent(event, eventType);
        } finally {
          if (!shouldSubscribeDocumentAnalysisEvents(event.status)) close();
        }
      }
    };
    listeners.set(eventType, listener);
    source.addEventListener(eventType, listener);
  }
  return close;
}

export const controlDocumentAnalysisRun = (
  recognitionRunId: string,
  action: DocumentAnalysisControlAction,
  expectedRevision: number,
  requestKey: string,
  reason: string,
  signal?: AbortSignal,
) => {
  const operationPath = action === "ranking_budget_enable"
    ? "ranking-budget/enable"
    : action === "ranking_budget_disable" ? "ranking-budget/disable" : action;
  return fetchAPI<DocumentAnalysisRunControl>(`${documentRunPath(recognitionRunId)}/${operationPath}`, {
    method: "POST",
    body: JSON.stringify({
      ...(action === "pause" ? {} : { expected_revision: expectedRevision }),
      request_key: requestKey,
      reason,
    }),
    signal,
  });
};

export const deleteDocumentAnalysisRun = (
  recognitionRunId: string,
  expectedRevision: number,
  requestKey: string,
  signal?: AbortSignal,
) => {
  const query = new URLSearchParams({
    expected_revision: String(expectedRevision),
    request_key: requestKey,
  });
  return fetchAPI<DocumentAnalysisRunControl>(`${documentRunPath(recognitionRunId)}?${query}`, {
    method: "DELETE",
    signal,
  });
};

// CMCReport PDE 冲突的人工决策：采纳推导 / 采纳原文 / 待复核。以 (job_id, conflict_key) 唯一，
// version 乐观并发（写入用 expected_version，冲突 → 409 VersionConflictError）。
export type PdeDecisionChoice = "derived" | "asserted" | "pending";

export interface PdeConflictDecision {
  job_id: string;
  conflict_key: string;
  chosen: PdeDecisionChoice;
  note: string;
  actor: string;
  version: number;
  decided_at: string | null;
}

export const getPdeConflictDecision = (jobId: string) =>
  fetchAPI<PdeConflictDecision>(`/api/extraction/jobs/${jobId}/pde-conflict/decision`);

export const decidePdeConflict = (
  jobId: string,
  body: { chosen: PdeDecisionChoice; note?: string; expected_version: number },
) =>
  fetchAPI<PdeConflictDecision>(`/api/extraction/jobs/${jobId}/pde-conflict/decision`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export async function generateRiskReport(
  jobId: string,
  templateId?: string,
): Promise<Blob | { report_id: string; status: string }> {
  const res = await fetch(`${API_BASE}/api/extraction/jobs/${jobId}/risk-report${templateId ? `?template_id=${encodeURIComponent(templateId)}` : ""}`, {
    method: "POST", headers: identityHeaders(),
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) {
    return res.json() as Promise<{ report_id: string; status: string }>;
  }
  return res.blob();
}

// 011 AST Coverage

export interface SlotCoverageDTO {
  slot_id: string;
  label: string;
  status: string;
  source_kind: string;
  value?: string | null;
  source_ref?: string | null;
  rule_key?: string | null;
  hazid?: string | null;
  note?: string | null;
  source_span?: string | null;
  is_llm_sourced?: boolean;
}

export interface GroupCoverageDTO {
  group_id: string;
  title: string;
  kind: string;
  slots: SlotCoverageDTO[];
  is_dynamic?: boolean;
}

export interface SectionCoverageDTO {
  section_id: string;
  title: string;
  groups: GroupCoverageDTO[];
}

export interface ASTCoverageDTO {
  template_id: string;
  template_name?: string;
  template_version?: string;
  total_slots: number;
  filled: number;
  inferred: number;
  missing_required: number;
  blank_optional: number;
  manual: number;
  dismissed: number;
  sections: SectionCoverageDTO[];
  snapshot_id?: string | null;
  manifest_id?: string | null;
  instance_manifest?: InstanceCoverageManifest | null;
}

export const getAstCoverage = (jobId: string, templateId?: string, signal?: AbortSignal) =>
  fetchAPI<ASTCoverageDTO>(
    `/api/extraction/jobs/${jobId}/ast-coverage${templateId ? `?template_id=${templateId}` : ""}`,
    { signal },
  );

// 015: per-section 行文 narrative prose, generated at report time and surfaced
// in the web reading pane. `subject_description`/`conclusion` are non-null only
// when LLM-generated (deterministic values stay out); `sections` is one entry
// per leaf section that carries a 行文 Prompt.
export interface ReportSectionNarrativeDTO {
  section_id: string;
  title: string;
  text: string;
}

export interface ReportNarrativesDTO {
  demonstration?: boolean;
  subject_description: string | null;
  conclusion: string | null;
  sections: ReportSectionNarrativeDTO[];
}

export interface GeneratedReportDTO {
  id: string;
  job_id: string;
  report_type: string;
  file_path: string;
  file_size: number | null;
  rules_fired_count: number;
  rules_summary: Record<string, unknown> | null;
  actor: string;
  report_run_id?: string | null;
  report_artifact_id?: string | null;
  demonstration?: boolean;
  created_at: string;
  // 015: present on the status-poll response; null for legacy/LLM-off reports.
  narratives?: ReportNarrativesDTO | null;
}

export const listReports = (jobId: string) =>
  fetchAPI<GeneratedReportDTO[]>(`/api/extraction/jobs/${jobId}/reports`);
export async function getReportRunArtifact(runId: string, artifactId: string, signal?: AbortSignal): Promise<Blob> {
  const response = await fetch(`${API_BASE}/api/report-runs/${encodeURIComponent(runId)}/artifacts/${encodeURIComponent(artifactId)}`, {
    headers: identityHeaders(), signal,
  });
  if (!response.ok) throw new Error(`API ${response.status}: ${await response.text()}`);
  return response.blob();
}
export const deleteReport = (jobId: string, reportId: string) =>
  fetchAPI<void>(`/api/extraction/jobs/${jobId}/reports/${reportId}`, { method: "DELETE" });

export async function dismissSlot(jobId: string, slotId: string): Promise<ASTCoverageDTO> {
  return fetchAPI<ASTCoverageDTO>(`/api/extraction/jobs/${jobId}/ast-coverage/dismiss`, {
    method: "POST", ...jsonBody({ slot_id: slotId }),
  });
}

export async function undismissSlot(jobId: string, slotId: string): Promise<ASTCoverageDTO> {
  return fetchAPI<ASTCoverageDTO>(
    `/api/extraction/jobs/${jobId}/ast-coverage/dismiss/${encodeURIComponent(slotId)}`,
    { method: "DELETE" },
  );
}

export async function downloadReport(jobId: string, reportId: string): Promise<Blob> {
  const res = await fetch(
    `${API_BASE}/api/extraction/jobs/${jobId}/reports/${reportId}/download`,
    { headers: identityHeaders() },
  );
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.blob();
}

export async function createAutoExtractionJob(params: {
  file: File;
  source_type: string;
  target_class_iris?: string[];
  doc_class_iri?: string;
  purpose?: "doc_repo_preview";
}): Promise<ExtractionJob> {
  const fd = new FormData();
  fd.append("file", params.file);
  fd.append("source_type", params.source_type);
  if (params.target_class_iris) {
    fd.append("target_class_iris", JSON.stringify(params.target_class_iris));
  }
  if (params.doc_class_iri) {
    fd.append("doc_class_iri", params.doc_class_iri);
  }
  if (params.purpose) {
    fd.append("purpose", params.purpose);
  }
  const res = await fetch(`${API_BASE}/api/extraction/jobs/auto`, {
    method: "POST", headers: identityHeaders(), body: fd,
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.json();
}

export interface SystemConfigEntry {
  key: string;
  value: unknown;
  updated_at: string | null;
}
export const getSystemConfig = (key: string) =>
  fetchAPI<SystemConfigEntry>(`/api/system-config/${encodeURIComponent(key)}`);
export const updateSystemConfig = (key: string, value: unknown) =>
  fetchAPI<SystemConfigEntry>(`/api/system-config/${encodeURIComponent(key)}`, {
    method: "PUT", ...jsonBody({ value }),
  });

export interface OntologyClassFlat {
  iri: string;
  name: string;
  label: string | null;
  module_key: string;
}
export const getAllClasses = () =>
  fetchAPI<OntologyClassFlat[]>("/api/ontology/all-classes");
export const getAllClassesWithSignal = (signal: AbortSignal) =>
  fetchAPI<OntologyClassFlat[]>("/api/ontology/all-classes", { signal });

// ===========================================================================
// 012 AST Template Management
// ===========================================================================

// tiptap/ProseMirror 文档 JSON（忠于原文结构的样例内容，供 WordViewer 渲染）。
export type TiptapContent = Record<string, unknown>;

export interface EvidenceAnchor {
  document_hash: string;
  parser_version: string;
  structure_hash: string;
  evidence_id: string;
  section_node_id: string;
  block_id: string;
  paragraph_index?: number | null;
  fragment_index?: number | null;
  table_path?: string[] | null;
  row_index?: number | null;
  column_index?: number | null;
  span_start?: number | null;
  span_end?: number | null;
  physical_page_number?: number | null;
}

export interface EvidenceCandidateRef {
  candidate_id: string;
  revision: number;
  class_iri?: string | null;
}

export interface EvidenceCandidate {
  candidate_id: string;
  revision: number;
  kind: "entity" | "property" | "relationship";
  text: string;
  class_iri?: string | null;
  class_label?: string | null;
  predicate_label?: string | null;
  identity?: Record<string, string>;
  path_root?: EvidenceCandidateRef | null;
  dependency_refs?: EvidenceCandidateRef[];
  predicate_iri?: string | null;
  subject?: EvidenceCandidateRef | null;
  object?: EvidenceCandidateRef | null;
  literal?: { raw_value: string; normalized_value: string | boolean | null; kind: string;
    datatype_iri: string; lower?: string | null; upper?: string | null; raw_unit?: string | null; canonical_unit?: string | null } | null;
  assertion_status: "affirmed" | "negated" | "conditional" | "hypothetical" | "uncertain";
  condition_anchors: EvidenceAnchor[];
  condition_provenance_indexes: number[];
  provenance: Array<{ kind: "document" | "external_record" | "manual" | "derived";
    anchors?: EvidenceAnchor[]; excerpts?: string[]; [key: string]: unknown }>;
  bindings: Array<{ method: string; anchors: EvidenceAnchor[]; [key: string]: unknown }>;
  validation_status: "pending" | "passed" | "rejected" | "conflict";
  validation_issues: Array<{ code: string; message: string }>;
  review_status: "pending" | "confirmed" | "rejected";
  review_source?: "automatic" | "manual" | null;
  review_reason?: string;
  commit_status: "not_requested" | "queued" | "applying" | "succeeded" | "failed";
  positive_eligible: boolean;
}

export interface EvidenceCommit {
  commit_id: string;
  status: "queued" | "applying" | "succeeded" | "failed";
  snapshot_id: string | null;
  attempts: number;
  error: string | null;
  items: EvidenceCandidateRef[];
}

export interface EvidenceClassSchema {
  label?: string | null;
  parents: string[];
  properties: Array<{ iri: string; label?: string | null }>;
  relationships: Array<{ iri: string; label?: string | null; range: string[] }>;
}

export interface EvidenceGraphSchema {
  document_class_iri: string | null;
  document_label?: string | null;
  classes: Record<string, EvidenceClassSchema>;
}

export interface EvidenceJob {
  candidates: EvidenceCandidate[];
  graph_schema?: EvidenceGraphSchema;
  calculations?: PDECalculation[];
  calculation_required?: boolean;
  execution_status?: string | null;
  run: { completion: "complete" | "incomplete"; diagnostics: string[];
    branch_progress?: Record<string, EvidenceBranchProgress> } | null;
  analysis_id: string | null;
  snapshot_id: string | null;
  commits: EvidenceCommit[];
  extraction_version?: { current: string; stored: string[]; outdated: boolean };
}

export interface EvidenceBranchProgress {
  status: "queued" | "extracting_entities" | "entities_failed" | "searching_entities"
    | "awaiting_relation" | "extracting_relation" | "relation_failed" | "relation_checked"
    | "identified" | "no_match";
  reason_codes: string[];
  discovery_tasks: number;
  relationship_tasks: number;
  failed_tasks: number;
  positive_count: number;
  coverage_complete: boolean;
  property_status?: "queued" | "extracting" | "incomplete" | "partial" | "complete" | "not_applicable";
  property_tasks?: number;
  positive_property_count?: number;
  failed_property_tasks?: number;
}

export interface PDECalculation {
  calculation_id: string; subject_candidate_id: string; subject_label: string;
  status: "passed" | "conflict" | "incomplete"; review_status: string;
  linked_to_source: boolean; blocks_conclusion: boolean; stale_decision: boolean;
  contract_ref: string; method_version: string; method_hash: string; formula: string; limitations: string;
  asserted_pde_mg_day: string | null; derived_pde_mg_day: string | null; effective_pde_mg_day: string | null;
  asserted_band: number | null; derived_band: number | null; ratio: string | null; ratio_threshold: string;
  inputs: Record<string, string>; factors: Record<string, { value: string; source: string }>;
  issues: { parameter: string; message: string }[];
  differences?: string[];
  input_evidence: Record<string, Array<{ candidate_id: string; revision: number;
    literal: { raw_value: string }; provenance: Array<{ anchors?: EvidenceAnchor[] }> }>>;
  decision_revision: number;
  decision: { choice: string; reason: string; actor: string; revision: number; decided_at: string } | null;
}

export const decideCalculation = (jobId: string, body: {
  subject_candidate_id: string; calculation_id: string; expected_revision: number;
  choice: "derived" | "asserted" | "rejected" | "pending"; reason: string;
}) => fetchAPI(`/api/extraction/jobs/${jobId}/calculations/decision`, { method: "POST", body: JSON.stringify(body) });

export const getJobEvidence = (jobId: string, signal?: AbortSignal, view: "all" | "latest_run" = "all") =>
  fetchAPI<EvidenceJob>(`/api/extraction/jobs/${jobId}/evidence?view=${view}`, { signal });

export interface InstanceCoverageTask {
  coverage_task_id?: string;
  target_id?: string;
  section_id?: string;
  label: string;
  status: "filled" | "missing" | "confirmed_absent" | "not_applicable" | "pending_review" | "conflict" | "incomplete" | "invalid" | "unavailable";
  reason: string;
  subject_instance_iri: string | null;
  subject_root_class_iri?: string | null;
  subject_path?: Array<{ predicate_iri: string; direction: "forward" | "inverse" }>;
  subject_candidate_ref?: EvidenceCandidateRef;
  predicate_path?: Array<{ predicate_iri: string; direction: "forward" | "inverse" }>;
  object_universe_status?: "complete" | "open" | "unresolved" | "unknown";
  objects?: Array<{ instance_iri: string; text: string; missing_properties: string[] }>;
  assertion_ids?: string[];
  negative_assertion_ids?: string[];
}

export interface InstanceCoverageManifest {
  availability: "available";
  manifest_id: string;
  template_id: string;
  template_version: string;
  snapshot_id: string | null;
  discovery_revision: string;
  material_status: "ready" | "invalid" | "conflict" | "incomplete";
  tasks: InstanceCoverageTask[];
  required_gaps: number;
  completion: "complete" | "incomplete";
  diagnostics: string[];
  gap_history?: Array<{ round: number; reason: string }>;
}

export interface EvidenceCoverageUnavailable {
  availability: "unavailable";
  template_id: string | null;
  template_version: string | null;
  manifest_id: null;
  required_gaps: null;
  error: { code: string; message: string; actual?: unknown };
}

export type EvidenceCoverage = InstanceCoverageManifest | EvidenceCoverageUnavailable;

export const getEvidenceCoverage = (jobId: string, templateId?: string, signal?: AbortSignal) =>
  fetchAPI<EvidenceCoverage>(`/api/extraction/jobs/${jobId}/evidence/coverage${templateId ? `?template_id=${encodeURIComponent(templateId)}` : ""}`, { signal });
export interface EvidenceExtractOptions {
  retry_failed?: boolean; reason?: string; pause_after?: number;
}
export const extractJobEvidence = (jobId: string, input?: EvidenceExtractOptions) =>
  fetchAPI<AnnotationRunReceipt>(`/api/extraction/jobs/${jobId}/evidence/extract`, {
  method: "POST", body: input ? JSON.stringify(input) : undefined,
});
export interface CoverageAction {
  manifest_id: string; template_id?: string; reason: string; coverage_task_ids?: string[];
}
export const fillEvidenceGaps = (jobId: string, input: CoverageAction) =>
  fetchAPI<{ reason: string; created: number; round?: number }>(`/api/extraction/jobs/${jobId}/evidence/fill-gaps`, {
    method: "POST", body: JSON.stringify(input),
  });
export const confirmEvidenceDiscovery = (jobId: string, input: CoverageAction) =>
  fetchAPI<InstanceCoverageManifest>(`/api/extraction/jobs/${jobId}/evidence/discovery`, {
    method: "POST", body: JSON.stringify(input),
  });
export const reviewEvidenceCandidate = (id: string, input: {
  expected_revision: number; decision: "confirmed" | "rejected"; reason: string;
  expected_review_status?: "pending" | "confirmed" | "rejected";
  edited_payload?: Record<string, unknown>;
}) => fetchAPI<EvidenceCandidate>(`/api/extraction/evidence/candidates/${id}/review`, {
  method: "PUT", body: JSON.stringify(input),
});
export const commitEvidence = (jobId: string, idempotencyKey: string, items: EvidenceCandidateRef[]) =>
  fetchAPI<EvidenceCommit>(`/api/extraction/jobs/${jobId}/evidence/commits`, {
    method: "POST", body: JSON.stringify({ idempotency_key: idempotencyKey, items }),
  });
export const retryEvidenceCommit = (id: string) =>
  fetchAPI<EvidenceCommit>(`/api/extraction/evidence/commits/${id}/retry`, { method: "POST" });

export interface DocumentEvidenceIR {
  document_hash: string;
  original_document_hash: string;
  parser_version: string;
  structure_hash: string;
  analysis_id: string;
  document_role: string;
  evidence_units: Array<{
    evidence_id: string; text: string; block_id: string; section_node_id: string;
    paragraph_index: number; fragment_index: number; table_path: string[] | null;
    row_index: number | null; column_index: number | null; physical_page_number: number | null;
  }>;
}

export interface TemplateOrigin {
  document_hash: string;
  parser_version: string;
  structure_hash: string;
  evidence_id: string;
  label_anchor: EvidenceAnchor;
  value_anchor: EvidenceAnchor | null;
}

// 015: lifecycle status + iri_pattern (functional doc-class resolution key,
// replaces the retired DocumentTypeMapping).
export type AstTemplateStatus = "draft" | "published" | "archived";

export interface StaticDemoProfile {
  fixture_id: "hrs5592-cmc-demo-v1";
  contract_id: "cmc-batch-demo-v1";
  document_iri: string;
}

export interface AstTemplateDTO {
  recognition_mode?: "ontology_guided" | "finder_legacy";
  finder_profile_id?: string | null;
  id: string;
  name: string;
  version: string;
  doc_no: string | null;
  iri_pattern: string | null;
  status: AstTemplateStatus;
  slot_count: number;
  demo_profile?: StaticDemoProfile | null;
  schema_version?: number;
  template_family_id?: string;
  revision_no?: number;
  schema_hash?: string;
  is_default: boolean;
  created_by: string | null;
  owner: string | null; // 015 责任人（业务负责人，区别于 created_by 创建者）
  sample_docx_filename: string | null;
  default_source_filename: string | null;
  default_source_job_id: string | null;
  created_at: string;
  updated_at: string | null;
}

export interface AstTemplateCreateInput {
  name: string;
  version?: string;
  doc_no?: string | null;
  iri_pattern?: string | null; // 015: doc-class resolution key
  schema_json: Record<string, unknown>;
  sample_text?: string | null;
  // 013: 忠于原文结构的 tiptap 样例——持久化后重新编辑时也能忠实预览。
  sample_content_json?: TiptapContent | null;
  sample_analysis?: DocumentEvidenceIR | null;
}

export interface AstTemplateUpdateInput {
  schema_json: Record<string, unknown>;
  version?: string | null;
}

// 015: in-place metadata edit — no version bump. 基本信息 tab edits
// name/doc_no/owner/status; the list-page ⋮ menu edits status/iri_pattern.
export interface AstTemplateMetaUpdateInput {
  name?: string;
  doc_no?: string | null;
  owner?: string | null;
  status?: AstTemplateStatus;
  iri_pattern?: string | null;
}

// 015 训练数据：源文档→评估报告 成对样例（report 可缺省）。
export interface TrainingPairDTO {
  id: string;
  source_filename: string;
  report_filename: string | null;
  created_at: string;
}

export const fetchAstTemplates = () =>
  fetchAPI<AstTemplateDTO[]>("/api/ast-templates");
export const getAstTemplate = (id: string, signal?: AbortSignal) =>
  fetchAPI<
    AstTemplateDTO & {
      schema_json: Record<string, unknown>;
      sample_text: string | null;
      sample_content_json: TiptapContent | null;
      sample_analysis?: DocumentEvidenceIR | null;
      structure_titles?: Record<string, string>;
      training_pairs: TrainingPairDTO[];
      versions: { id: string; version: string; created_at: string }[];
    }
  >(`/api/ast-templates/${id}`, { signal });
export const createAstTemplate = (data: AstTemplateCreateInput) =>
  saveTemplateRequest<AstTemplateDTO>("/api/ast-templates", jsonBody(data));
export const updateAstTemplate = (id: string, data: AstTemplateUpdateInput) =>
  fetchAPI<AstTemplateDTO>(`/api/ast-templates/${id}`, { method: "PUT", ...jsonBody(data) });
export const deleteAstTemplate = (id: string) =>
  fetchAPI<void>(`/api/ast-templates/${id}`, { method: "DELETE" });
export const setDefaultTemplate = (id: string) =>
  fetchAPI<AstTemplateDTO>(`/api/ast-templates/${id}/set-default`, { method: "POST" });
// 015: PATCH in-place metadata (status / iri_pattern) without a version bump.
export const updateAstTemplateMeta = (id: string, data: AstTemplateMetaUpdateInput) =>
  fetchAPI<AstTemplateDTO>(`/api/ast-templates/${id}`, { method: "PATCH", ...jsonBody(data) });
export interface TemplateRecognitionEngine {
  recognition_mode: "ontology_guided" | "finder_legacy";
  finder_profile_id: string | null;
  finder_profiles: { id: string; label: string }[];
}

export interface TemplateRecognitionEngineUpdate {
  recognition_mode: TemplateRecognitionEngine["recognition_mode"];
  finder_profile_id: string | null;
  expected_recognition_mode: TemplateRecognitionEngine["recognition_mode"];
  expected_finder_profile_id: string | null;
}

export const getTemplateRecognitionEngine = (id: string, signal?: AbortSignal) =>
  fetchAPI<TemplateRecognitionEngine>(`/api/ast-templates/${id}/recognition-engine`, { signal });
export const updateTemplateRecognitionEngine = (id: string, data: TemplateRecognitionEngineUpdate) =>
  fetchAPI<TemplateRecognitionEngine>(`/api/ast-templates/${id}/recognition-engine`, {
    method: "PATCH", ...jsonBody(data),
  });

// 013: 后台把样例 DOCX 解析为忠于原文结构的 tiptap（不扁平化成文本），前端据此
// 忠实预览并回传结构化内容做 AI 分析——避免「解析成文本→送前台→送回」丢结构。
export interface ParseSampleResult {
  content_json: TiptapContent;
  plain_text: string;
  analysis?: DocumentEvidenceIR;
}

export async function parseSample(file: File, signal?: AbortSignal): Promise<ParseSampleResult> {
  const form = new FormData();
  form.append("file", file);
  const controller = new AbortController();
  const cancel = () => controller.abort(signal?.reason);
  if (signal?.aborted) cancel();
  else signal?.addEventListener("abort", cancel, { once: true });
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, 180_000);
  try {
    return await fetchAPI<ParseSampleResult>("/api/ast-templates/parse-sample", {
      method: "POST", body: form, signal: controller.signal,
    });
  } catch (error) {
    if (timedOut) throw new Error("样例文档解析超时，请重试；较大的文档可先另存为 .docx 后上传。");
    throw error;
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", cancel);
  }
}

// 015 基本信息 tab：默认示例文档替换 / 默认源文件 / 训练数据（源文档→评估报告）。
// 均为 multipart，复用 identityHeaders()（勿手设 Content-Type，交给浏览器带 boundary）。
async function postMultipart<T>(path: string, form: FormData, method = "POST"): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: identityHeaders(),
    body: form,
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return (await res.json()) as T;
}

// 替换默认模板示例文档（固化输出 section/格式）；返回解析后的忠实预览内容。
export async function uploadTemplateSample(
  id: string,
  file: File,
): Promise<ParseSampleResult> {
  const form = new FormData();
  form.append("file", file);
  return postMultipart<ParseSampleResult>(`/api/ast-templates/${id}/sample`, form);
}

// 创建向导只需要持久化确认；避免再次下载、解析整份样例 JSON。
export async function saveTemplateSample(id: string, file: File): Promise<void> {
  const form = new FormData();
  form.append("file", file);
  return saveTemplateRequest<void>(`/api/ast-templates/${id}/sample?include_content=false`, {
    body: form,
  });
}

async function saveTemplateRequest<T>(path: string, options: RequestInit): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, 180_000);
  try {
    return await fetchAPI<T>(path, { ...options, method: "POST", signal: controller.signal });
  } catch (error) {
    if (timedOut) throw new Error("保存等待超时，服务端可能仍在处理。请先返回列表核对保存结果，勿重复创建模板。");
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

// 默认源文件（固化输出格式的参照原件）上传 / 清除。
export async function uploadDefaultSource(id: string, file: File): Promise<AstTemplateDTO> {
  const form = new FormData();
  form.append("file", file);
  return postMultipart<AstTemplateDTO>(`/api/ast-templates/${id}/default-source`, form);
}
export const deleteDefaultSource = (id: string) =>
  fetchAPI<AstTemplateDTO>(`/api/ast-templates/${id}/default-source`, { method: "DELETE" });

// 训练数据 CRUD：源文档必填，评估报告可缺省。
export const listTrainingPairs = (id: string) =>
  fetchAPI<TrainingPairDTO[]>(`/api/ast-templates/${id}/training-pairs`);
export async function uploadTrainingPair(
  id: string,
  source: File,
  report?: File | null,
): Promise<TrainingPairDTO> {
  const form = new FormData();
  form.append("source_file", source);
  if (report) form.append("report_file", report);
  return postMultipart<TrainingPairDTO>(`/api/ast-templates/${id}/training-pairs`, form);
}
export const deleteTrainingPair = (id: string, pairId: string) =>
  fetchAPI<void>(`/api/ast-templates/${id}/training-pairs/${pairId}`, { method: "DELETE" });

// --------------------------------------------------------------------------- //
// 013 LLM Template Design Assist + Report Enhancement
// --------------------------------------------------------------------------- //

// 016: section-level ontology coverage (mirrors backend OntologyRelationBinding /
// FactSourceBinding / CoverageDeclaration). All IRIs are class/predicate TYPES —
// never a sample individual (FR-003 / SC-002).
export interface OntologyRelationBinding {
  kind: "ontology_relation";
  doc_class_iri: string;
  predicate_iri: string;
  range_class_iri: string;
  required: boolean; // default true (FR-005a)
  required_properties?: string[];
  label?: string;
  predicate_path?: Array<{ predicate_iri: string; direction: "forward" | "inverse" }>;
  quantifier?: "exists" | "all";
  min_count?: number;
  max_count?: number | null;
  subject_instance_iris?: string[];
  subject_root_class_iri?: string | null;
  subject_path?: Array<{ predicate_iri: string; direction: "forward" | "inverse" }>;
  object_instance_iris?: string[] | null;
  applicable_at?: string | null;
}
export interface FactSourceBinding {
  kind: "fact_source";
  source: string;
  selector?: string;
  label?: string;
}
export type CoverageBinding = OntologyRelationBinding | FactSourceBinding;

export interface SuggestSlotsRequest {
  job_id?: string | null;
  document_text?: string | null;
  // 013: 结构化样例（tiptap）——首选输入，服务端派生 LLM 文本与 source_ref 锚点。
  sample_content_json?: TiptapContent | null;
  analysis?: DocumentEvidenceIR | null;
  existing_template?: Record<string, unknown> | null;
  max_suggestions?: number;
  // 016 (D10): document entity type grounds ontology coverage; augments a source.
  doc_class_iri?: string | null;
}

// AI Round-1 结构骨架（后端逐字回传，形状 = slot_suggester._ROUND1_SCHEMA）。无本体
// IRI 绑定——编辑器把每个 candidate 物化为可作者填写的 semantic 槽。
export interface AiStructureCandidate {
  id: string;
  origin: TemplateOrigin;
  label: string;
  semantic_label?: string;
  evidence_span?: string;
  evidence_offset?: number;
}
export interface AiStructureGroup {
  id: string;
  origin: TemplateOrigin | null;
  title: string;
  candidates: AiStructureCandidate[];
}
export interface AiStructureSection {
  id: string;
  origin: TemplateOrigin | null;
  title: string;
  groups: AiStructureGroup[];
}

// AI 分析输出：文档结构骨架（sections）+ 本体覆盖边（coverage）。
export interface SuggestSlotsResponse {
  completion: "complete" | "incomplete";
  degraded: boolean;
  diagnostics: string[];
  document_summary: string;
  // 016: ontology-grounded coverage only (US1). 无法绑定到菜单关系边的位点静默忽略，
  // 不再返回 unresolved_candidates（取代 FR-008a）。
  coverage: CoverageBinding[];
  // Round-1 结构骨架；空模板首次分析时物化为分节 + 语义化槽（见 template-slot-editor
  // 的 materializeSkeleton）。带 IRI 绑定的旧 llm_extraction 取数流不随此字段回归。
  sections?: AiStructureSection[];
}

export const suggestSlots = (data: SuggestSlotsRequest) =>
  fetchAPI<SuggestSlotsResponse>("/api/ast-templates/suggest-slots", {
    method: "POST",
    ...jsonBody(data),
  });

// 016：候选文档类型中「已建模可覆盖关系」（≥1 条 hop-1 边）的子集。作者化 UI 据此
// 门控「关联文档类型」下拉——仅启用已建模类型（当前仅 CMC 报告，本体补充关系后自动扩大）。
export interface CoverageDocClassesResponse {
  capable: string[];
}
export const getCoverageDocClasses = (docClassIris: string[]) =>
  fetchAPI<CoverageDocClassesResponse>(
    "/api/ast-templates/coverage-doc-classes",
    { method: "POST", ...jsonBody({ doc_class_iris: docClassIris }) },
  );

// 015: design-time — derive a reusable 行文 Prompt for one section from the
// sample + the section's slot labels. Gated identically to suggest-slots.
export interface GenerateSectionPromptRequest {
  section_title: string;
  slot_labels?: string[];
  sample_text?: string;
  instructions?: string;
}

export const generateSectionPrompt = (data: GenerateSectionPromptRequest, signal?: AbortSignal) =>
  fetchAPI<{ prompt: string }>("/api/ast-templates/generate-section-prompt", {
    method: "POST",
    signal,
    ...jsonBody(data),
  });

// 015+: preview the prose one section's (possibly-unsaved) 行文 Prompt produces,
// from a matched document's REAL extracted facts (same path as the report). Needs
// a source job associated in the「源文档」tab; gated identically to suggest-slots.
export interface PreviewSectionNarrativeRequest {
  job_id: string;
  template_id: string;
  section_id: string;
  prompt: string;
  draft_schema?: import("./reporting-v2").TemplateV2;
}

export interface SectionNarrativePreview {
  narrative: string;
  warnings: string[];
  source: { kind: string; job_id: string; execution_id: string; source_filename: string };
}

export const previewSectionNarrative = (
  data: PreviewSectionNarrativeRequest,
  signal?: AbortSignal,
) =>
  fetchAPI<SectionNarrativePreview>(
    "/api/ast-templates/preview-section-narrative",
    { method: "POST", signal, ...jsonBody(data) },
  );

export const pollReportStatus = (jobId: string, reportId: string) =>
  fetchAPI<GeneratedReportDTO & { report_status?: string; report_error?: string }>(
    `/api/extraction/jobs/${jobId}/reports/${reportId}`,
  );

export async function downloadReportById(
  jobId: string,
  reportId: string,
): Promise<Blob> {
  const res = await fetch(
    `${API_BASE}/api/extraction/jobs/${jobId}/reports/${reportId}/download`,
    { headers: identityHeaders() },
  );
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.blob();
}

// ===========================================================================
// 015 read-only UI composition helpers (NO new endpoint — FR-027).
// Each helper is a pure projection over the existing API surface, consumed by
// the new operational pages (Connector / Approval / Report Center).
// ===========================================================================

// --- T032 Connector status mapping (US2 · FR-012 · research R4/R5) ----------
/**
 * Semantic connection status a Connector card renders. Derived from the
 * backend's `last_status` / `last_error`; air-gap-from-public-cloud is a
 * NORMAL state (never fabricated as an error) — only a genuinely failed run /
 * `last_error` yields `failed`.
 */
export type ConnectorStatus = "connected" | "connecting" | "failed" | "not-connected";

export function connectorStatus(c: Pick<Connector, "last_status" | "last_error">): ConnectorStatus {
  if (c.last_error) return "failed";
  const s = (c.last_status ?? "").toLowerCase();
  if (!s) return "not-connected";
  if (["failed", "error", "unreachable"].some((k) => s.includes(k))) return "failed";
  if (["running", "syncing", "connecting", "in_progress", "pending"].some((k) => s.includes(k)))
    return "connecting";
  if (["ok", "success", "connected", "succeeded", "synced", "completed"].some((k) => s.includes(k)))
    return "connected";
  // Unknown non-empty status → treat as connected-with-caveat rather than error.
  return "connected";
}

/** zh-CN label + which semantic token family a status uses (success/warning/destructive/muted). */
export const CONNECTOR_STATUS_META: Record<
  ConnectorStatus,
  { label: string; tone: "success" | "warning" | "destructive" | "muted" }
> = {
  connected: { label: "已连接", tone: "success" },
  connecting: { label: "连接中", tone: "warning" },
  failed: { label: "连接失败", tone: "destructive" },
  "not-connected": { label: "未连接", tone: "muted" },
};

/** Group a flat connector list by `system_type` (FR-010), preserving order. */
export function groupConnectorsByType(connectors: Connector[]): Array<{ type: string; connectors: Connector[] }> {
  const order: string[] = [];
  const buckets = new Map<string, Connector[]>();
  for (const c of connectors) {
    const key = c.system_type || "other";
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key)!.push(c);
  }
  return order.map((type) => ({ type, connectors: buckets.get(type)! }));
}

// --- T047 Approval queue mapping (US4 · FR-017 · research R3) ----------------
export type ApprovalUrgency = "high" | "medium" | "low";

export interface ApprovalDocument {
  name: string;
  format: string;
}

export interface ApprovalTimelineEntry {
  action: string;
  actor: string;
  time: string;
}

export interface SystemRecommendation {
  action: "approve" | "reject";
  reason: string;
  pdeAnalysis?: {
    reportedPDE: string;
    calculatedPDE: string;
    noael: string;
    noaelSource: string;
    bodyWeight: number;
    factors: Record<string, number>;
    factorLabels: Record<string, string>;
    oebReported: string;
    oebCalculated: string;
    deviation: string;
  };
}

export interface ApprovalTask {
  id: string;
  title: string;
  referenceNo: string;
  category: string;
  type: string;
  urgency: ApprovalUrgency;
  submitter: string;
  submittedAt: string;
  submittedLabel: string;
  entityName: string;
  entityType: string;
  phase: string;
  status: "pending" | "completed";
  unread?: boolean;
  documents: ApprovalDocument[];
  timeline: ApprovalTimelineEntry[];
  systemRecommendation?: SystemRecommendation;
  risk_level: string | null;
  execution_type: string;
}

export const APPROVAL_CATEGORIES = [
  "临床前研究审批",
  "IND 申报审批",
  "临床试验审批",
  "NDA 注册审批",
  "GMP 生产审批",
  "药理毒理变更审批",
  "药物警戒审批",
] as const;

const MOCK_APPROVAL_TASKS: ApprovalTask[] = [
  {
    id: "APR-2026-0618",
    title: "原料药 HRS-5678 临床备样生产信息-风险评估",
    referenceNo: "QS-A-020F05-001",
    category: "临床前研究审批",
    type: "非临床安全性评价",
    urgency: "high",
    submitter: "李婷",
    submittedAt: "2026-07-03T08:24:00",
    submittedLabel: "2 小时前提交",
    entityName: "化合物 HRS-5678 (ActiveIngredient)",
    entityType: "ActiveIngredient",
    phase: "临床前研究 — 毒理学评价",
    status: "pending",
    unread: true,
    risk_level: "Band 5",
    execution_type: "非临床安全性评价",
    documents: [
      { name: "原料药 HRS-5678 临床备样生产信息.docx", format: "DOCX" },
      { name: "遗传毒性试验总结报告.docx", format: "DOCX" },
      { name: "安全药理学评价数据.xlsx", format: "XLSX" },
    ],
    timeline: [
      { action: "提交申请", actor: "李婷", time: "07-03 10:24" },
    ],
    systemRecommendation: {
      action: "reject",
      reason: "报告 PDE 值与系统推算结果偏差 18,000 倍，OEB 等级应为 Band 5（极高危害），疑似安全系数遗漏或 NOAEL 引用错误，建议驳回。",
      pdeAnalysis: {
        reportedPDE: "180 mg/day",
        calculatedPDE: "10 μg/day",
        noael: "0.5 mg/kg/day",
        noaelSource: "28天重复给药毒性试验（大鼠）",
        bodyWeight: 50,
        factors: { F1: 5, F2: 10, F3: 5, F4: 1, F5: 10 },
        factorLabels: {
          F1: "种属差异（大鼠→人）",
          F2: "个体差异",
          F3: "亚慢性→慢性外推",
          F4: "无严重毒性（NOAEL 可用）",
          F5: "遗传毒性关注",
        },
        oebReported: "Band 2（报告隐含）",
        oebCalculated: "Band 5（极高危害）",
        deviation: "18,000 倍",
      },
    },
  },
  {
    id: "APR-2026-0615",
    title: "API 工艺验证批记录审核",
    referenceNo: "APR-2026-0615",
    category: "GMP 生产审批",
    type: "原料药工艺审批",
    urgency: "medium",
    submitter: "王强",
    submittedAt: "2026-07-02T14:30:00",
    submittedLabel: "昨天 14:30",
    entityName: "XR-7742 API 工艺 (ManufacturingProcess)",
    entityType: "ManufacturingProcess",
    phase: "工艺验证 — 批记录审核",
    status: "pending",
    risk_level: null,
    execution_type: "原料药工艺审批",
    documents: [
      { name: "工艺验证批记录-批号2026B003.pdf", format: "PDF" },
      { name: "中间体检测报告.xlsx", format: "XLSX" },
    ],
    timeline: [
      { action: "提交申请", actor: "王强", time: "07-02 14:30" },
    ],
  },
  {
    id: "APR-2026-0612",
    title: "IND 申请 CTD 模块三审核",
    referenceNo: "APR-2026-0612",
    category: "IND 申报审批",
    type: "IND 申请文件审批",
    urgency: "high",
    submitter: "赵燕",
    submittedAt: "2026-06-30T09:15:00",
    submittedLabel: "06-30",
    entityName: "XR-7742 IND 申请 (RegulatorySubmission)",
    entityType: "RegulatorySubmission",
    phase: "IND 申报 — CTD 文件编制",
    status: "pending",
    risk_level: null,
    execution_type: "IND 申请文件审批",
    documents: [
      { name: "CTD 模块三品质文件.pdf", format: "PDF" },
      { name: "药学研究资料汇总.docx", format: "DOCX" },
    ],
    timeline: [
      { action: "提交申请", actor: "赵燕", time: "06-30 09:15" },
    ],
  },
  {
    id: "APR-2026-0610",
    title: "药理学研究方案变更审批",
    referenceNo: "APR-2026-0610",
    category: "药理毒理变更审批",
    type: "药理毒理变更",
    urgency: "low",
    submitter: "张明",
    submittedAt: "2026-06-28T16:00:00",
    submittedLabel: "06-28",
    entityName: "XR-7742 药理研究 (PharmacologyStudy)",
    entityType: "PharmacologyStudy",
    phase: "药理学评价 — 方案变更",
    status: "pending",
    risk_level: null,
    execution_type: "药理毒理变更",
    documents: [
      { name: "药理学研究方案变更申请表.pdf", format: "PDF" },
    ],
    timeline: [
      { action: "提交申请", actor: "张明", time: "06-28 16:00" },
    ],
  },
  {
    id: "APR-2026-0608",
    title: "化合物 XR-7742 安全药理学评价",
    referenceNo: "APR-2026-0608",
    category: "临床前研究审批",
    type: "安全药理学评价",
    urgency: "medium",
    submitter: "陈工",
    submittedAt: "2026-06-27T11:00:00",
    submittedLabel: "06-27",
    entityName: "化合物 XR-7742 (ActiveIngredient)",
    entityType: "ActiveIngredient",
    phase: "临床前研究 — 安全药理学",
    status: "pending",
    risk_level: "Band 3",
    execution_type: "安全药理学评价",
    documents: [
      { name: "安全药理学研究报告.pdf", format: "PDF" },
      { name: "心血管安全性评价数据.xlsx", format: "XLSX" },
    ],
    timeline: [
      { action: "提交申请", actor: "陈工", time: "06-27 11:00" },
    ],
  },
];

export async function getApprovalTasks(): Promise<ApprovalTask[]> {
  return MOCK_APPROVAL_TASKS;
}

export function approvalCategoryCounts(tasks: ApprovalTask[]): Array<{ name: string; count: number }> {
  const counts = new Map<string, number>();
  for (const cat of APPROVAL_CATEGORIES) counts.set(cat, 0);
  for (const t of tasks) counts.set(t.category, (counts.get(t.category) ?? 0) + 1);
  return Array.from(counts.entries()).map(([name, count]) => ({ name, count }));
}

// --- T056 Report + Document unified aggregation (US5 · FR-021 · research R2) -
export type ReportKind = "generated-report" | "uploaded-document";

/** Normalized unified item rendered by Report Center / opened in Report Detail. */
export interface ReportOrDocument {
  /** Stable UI key: report id or document iri. */
  key: string;
  kind: ReportKind;
  title: string;
  category: string;
  /** Format/type label (report_type, or document type). */
  type: string;
  date: string | null;
  size: number | null;
  /** Generated-report coordinates (present only for kind==="generated-report"). */
  jobId?: string;
  reportId?: string;
  /** Document coordinates (present only for kind==="uploaded-document"). */
  iri?: string;
  /** Processing status — "processing" for optimistic uploads not yet in backend. */
  status?: "processing" | "ready";
}

export interface ReportCenterPage {
  items: ReportOrDocument[];
  total: number;
  page: number;
  page_size: number;
}

export interface GeneratedReportSummary {
  id: string;
  job_id: string;
  source_filename: string | null;
  report_type: string;
  file_size: number | null;
  created_at: string;
}

export async function listReportCenterDocuments(signal?: AbortSignal): Promise<ReportOrDocument[]> {
  const docs = await listDocuments(undefined, 100, signal);
  return docs.items.map((d) => {
    const phase = (d.properties_json?.hasDevelopmentPhase as string) ?? null;
    return {
      key: d.iri,
      kind: "uploaded-document",
      title: d.label_zh || d.label_en || d.iri.split("/").pop() || d.iri,
      // 文件夹（category）= 研发阶段（左侧分类轴）；类型（type）= 文档类，列展示。
      category: phase ? phaseLabel(phase) : "未分阶段",
      type: docTypeLabel(d.class_iri) || "文档",
      date: (d.properties_json?.created_at as string) ?? (d.properties_json?.ingested_at as string) ?? null,
      size: null,
      iri: d.iri,
    };
  });
}

/** One bounded summary page; details and report bodies are fetched only when opened. */
export async function listReportCenterReports(page = 1, signal?: AbortSignal): Promise<ReportCenterPage> {
  const result = await fetchAPI<Omit<ReportCenterPage, "items"> & { items: GeneratedReportSummary[] }>(
    `/api/reports?page=${page}&page_size=25`,
    { signal },
  );
  return {
    ...result,
    items: result.items.map((r) => ({
      key: r.id,
      kind: "generated-report",
      title: `${r.report_type === "batch_record_demo" ? "批记录报告（演示）" : r.report_type}（${r.source_filename ?? r.job_id.slice(0, 8)}）`,
      category: r.report_type,
      type: r.report_type === "batch_record_demo" ? "批记录报告（演示）" : r.report_type,
      date: r.created_at,
      size: r.file_size,
      jobId: r.job_id,
      reportId: r.id,
    })),
  };
}

/** Preserve query-less detail links using summaries, without loading task/report bodies. */
export async function resolveReportCenterItem(key: string, signal?: AbortSignal): Promise<ReportOrDocument | null> {
  if (!/^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}$/i.test(key)) {
    return (await listReportCenterDocuments(signal)).find((item) => item.key === key) ?? null;
  }
  for (let page = 1; ; page++) {
    const result = await listReportCenterReports(page, signal);
    const item = result.items.find((entry) => entry.key === key);
    if (item) return item;
    if (result.page * result.page_size >= result.total || result.items.length === 0) return null;
  }
}

// ---------------------------------------------------------------------------
// Mock 外部事实源数据
// ---------------------------------------------------------------------------

export interface MockDepartment {
  id?: string;
  code: string;
  iri: string;
  label: string;
  description: string;
  data_properties: Array<{ iri: string | null; label: string; value: string }>;
}

export interface MockRole {
  id?: string;
  code: string;
  iri: string;
  label: string;
  role_class_iri: string;
  description: string;
  data_properties: Array<{ iri: string | null; label: string; value: string }>;
}

export interface MockEquipment {
  id?: string;
  equipment_id: string;
  iri: string;
  label: string;
  equipment_class_iri: string;
  workshop_code: string;
  data_properties: Array<{ iri: string | null; label: string; value: string }>;
}

export interface MockEquipmentSchedule {
  id: string;
  task_id: string;
  task_name: string;
  activity_type: "production" | "cleaning" | "setup" | "maintenance";
  status: "scheduled" | "running" | "completed" | "paused" | "cancelled";
  priority: "low" | "normal" | "high" | "urgent";
  product_id: string | null;
  product_code: string | null;
  product_name: string | null;
  batch_no: string | null;
  planned_quantity: number | null;
  quantity_unit: string | null;
  equipment_id: string;
  equipment_name: string;
  workshop_code: string;
  start_at: string;
  end_at: string;
  actual_start_at: string | null;
  actual_end_at: string | null;
  process_step: string | null;
  operator_team: string | null;
  progress: number;
  remark: string | null;
}

export interface MockProductionArea {
  id?: string;
  code: string;
  iri: string;
  label: string;
  description: string;
  data_properties: Array<{ iri: string | null; label: string; value: string }>;
}

export interface MockTeamMember {
  id?: string;
  team_type?: string;
  name: string;
  role_label: string;
  department: string;
  role_class_iri: string;
  role_code: string;
}

export async function listMockDepartments(): Promise<MockDepartment[]> {
  const r = await fetch(`${API_BASE}/api/mock-sources/departments`, { headers: identityHeaders() });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function createMockDepartment(dept: MockDepartment): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/departments`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify(dept),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function updateMockDepartment(id: string, dept: MockDepartment): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/departments/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify(dept),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function deleteMockDepartment(id: string): Promise<void> {
  const r = await fetch(`${API_BASE}/api/mock-sources/departments/${id}`, {
    method: "DELETE",
    headers: identityHeaders(),
  });
  if (!r.ok) throw new Error(await r.text());
}

export async function listMockRoles(): Promise<MockRole[]> {
  const r = await fetch(`${API_BASE}/api/mock-sources/roles`, { headers: identityHeaders() });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function createMockRole(role: MockRole): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/roles`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify(role),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function updateMockRole(id: string, role: MockRole): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/roles/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify(role),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function deleteMockRole(id: string): Promise<void> {
  const r = await fetch(`${API_BASE}/api/mock-sources/roles/${id}`, {
    method: "DELETE",
    headers: identityHeaders(),
  });
  if (!r.ok) throw new Error(await r.text());
}

export async function listMockEquipment(): Promise<MockEquipment[]> {
  const r = await fetch(`${API_BASE}/api/mock-sources/equipment`, { headers: identityHeaders() });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function listMockEquipmentSchedules(
  equipmentId: string,
  startDate: string,
  endDate: string,
): Promise<MockEquipmentSchedule[]> {
  const params = new URLSearchParams({
    equipment_id: equipmentId,
    start_date: startDate,
    end_date: endDate,
  });
  const r = await fetch(`${API_BASE}/api/mock-sources/equipment-schedules?${params}`, {
    headers: identityHeaders(),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function updateMockEquipmentScheduleOccupancy(input: {
  equipment_id: string;
  schedule_date: string;
  product_code: "HRS-5678" | "HRS-1597";
}): Promise<{ id: string; equipment_id: string; schedule_date: string; product_code: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/equipment-schedules/occupancy`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify(input),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function createMockEquipment(equip: MockEquipment): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/equipment`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify(equip),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function updateMockEquipment(id: string, equip: MockEquipment): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/equipment/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify(equip),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function deleteMockEquipment(id: string): Promise<void> {
  const r = await fetch(`${API_BASE}/api/mock-sources/equipment/${id}`, {
    method: "DELETE",
    headers: identityHeaders(),
  });
  if (!r.ok) throw new Error(await r.text());
}

export async function listMockProductionAreas(): Promise<MockProductionArea[]> {
  const r = await fetch(`${API_BASE}/api/mock-sources/production-areas`, { headers: identityHeaders() });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function createMockProductionArea(area: MockProductionArea): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/production-areas`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify(area),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function updateMockProductionArea(id: string, area: MockProductionArea): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/production-areas/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify(area),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function deleteMockProductionArea(id: string): Promise<void> {
  const r = await fetch(`${API_BASE}/api/mock-sources/production-areas/${id}`, {
    method: "DELETE",
    headers: identityHeaders(),
  });
  if (!r.ok) throw new Error(await r.text());
}

export async function listMockAssessmentTeam(): Promise<MockTeamMember[]> {
  const r = await fetch(`${API_BASE}/api/mock-sources/assessment-team`, { headers: identityHeaders() });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function createMockAssessmentTeamMember(member: MockTeamMember): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/assessment-team`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify({ ...member, team_type: "assessment" }),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function updateMockAssessmentTeamMember(id: string, member: MockTeamMember): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/assessment-team/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify({ ...member, team_type: "assessment" }),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function deleteMockAssessmentTeamMember(id: string): Promise<void> {
  const r = await fetch(`${API_BASE}/api/mock-sources/assessment-team/${id}`, {
    method: "DELETE",
    headers: identityHeaders(),
  });
  if (!r.ok) throw new Error(await r.text());
}

export async function listMockApproverTeam(): Promise<MockTeamMember[]> {
  const r = await fetch(`${API_BASE}/api/mock-sources/approver-team`, { headers: identityHeaders() });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function createMockApproverTeamMember(member: MockTeamMember): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/approver-team`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify({ ...member, team_type: "approver" }),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function updateMockApproverTeamMember(id: string, member: MockTeamMember): Promise<{ id: string }> {
  const r = await fetch(`${API_BASE}/api/mock-sources/approver-team/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...identityHeaders() },
    body: JSON.stringify({ ...member, team_type: "approver" }),
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export async function deleteMockApproverTeamMember(id: string): Promise<void> {
  const r = await fetch(`${API_BASE}/api/mock-sources/approver-team/${id}`, {
    method: "DELETE",
    headers: identityHeaders(),
  });
  if (!r.ok) throw new Error(await r.text());
}

// Versioned CMC demo graph. Discovery and preview are read-only; generation is explicit.
export interface BatchDemoLayout {
  kind: "template_layout";
  role?: string;
  width_pt?: number;
  height_pt?: number;
  top_pt?: number;
  bottom_pt?: number;
  left_pt?: number;
  right_pt?: number;
  widths?: number[];
  span?: number;
  row_span?: number;
  vmerge?: "restart" | "continue";
  vertical_align?: "top" | "center" | "bottom" | null;
  border_top?: string;
  border_right?: string;
  border_bottom?: string;
  border_left?: string;
  font_size_pt?: number;
  line_height_pt?: number;
  underline?: boolean;
  space_before_pt?: number;
  space_after_pt?: number;
  font_family?: string;
  latin_font_family?: string;
  italic?: boolean;
  script?: string | null;
  align?: "left" | "center" | "right" | "justify";
  bold?: boolean;
  renderer_version?: string;
  paragraphs?: Array<{
    style: Partial<BatchDemoLayout>;
    space_before_pt: number;
    space_after_pt: number;
    runs: Array<{ text: string; style: Partial<BatchDemoLayout> }>;
  }>;
}
export interface BatchDemoOutputNode {
  node_id: string;
  kind: string;
  text: string;
  children: BatchDemoOutputNode[];
  provenance_refs?: Array<BatchDemoLayout | { kind: string }>;
  header?: boolean;
}
export interface BatchDemoReport {
  id: string;
  job_id: string;
  report_type: "batch_record_demo";
  report_status: string;
  created_at: string | null;
  file_size: number;
  graph_hash: string;
  template_hash: string;
  stages: Array<{ key: string; label: string; status: string; detail: string }>;
  body_ast: BatchDemoOutputNode;
}
export interface BatchDemoAvailable {
  available: true;
  document_iri: string;
  source_job_id: string;
  graph_hash: string;
  template_hash: string;
  graph: {
    fixture_id: string;
    source_filename: string;
    source_sha256: string;
    doc_class: DocClassification;
    relationships: Relationship[];
    warnings: string[];
  };
  template: {
    template_id: string;
    name: string;
    version: string;
    manual_fields: string[];
    layout?: { reference_template_id: string; reference_name: string; sample_sha256: string };
    sections: Array<{ id: string; title: string; path: string[]; mode: string }>;
  };
  validation: {
    passed: boolean;
    checks: Array<{ id: string; label: string; path: string[]; count: number; passed: boolean; errors: string[] }>;
  };
  latest_report: BatchDemoReport | null;
  preview_ast: BatchDemoOutputNode;
}
export type BatchDemoContext = BatchDemoAvailable | { available: false };
export const getBatchDemo = (documentIri: string, signal?: AbortSignal) =>
  fetchAPI<BatchDemoContext>(`/api/reports/batch-demo?document_iri=${encodeURIComponent(documentIri)}`, { signal });
export const getBatchDemoTemplate = (templateId: string, signal?: AbortSignal) =>
  fetchAPI<BatchDemoAvailable>(`/api/reports/batch-demo/templates/${encodeURIComponent(templateId)}`, { signal });
export const generateBatchDemo = (
  documentIri: string,
  body: { graph_hash: string; template_hash: string; request_key: string },
) => fetchAPI<BatchDemoReport>(`/api/reports/batch-demo?document_iri=${encodeURIComponent(documentIri)}`, {
  method: "POST", body: JSON.stringify(body),
});

// Expert feedback is stored separately from graph decisions and calibration approval.
export interface ExpertOpinionTarget {
  document_iri?: string | null;
  job_id?: string | null;
  report_id?: string | null;
}
export interface ExpertOpinionContext {
  target: ExpertOpinionTarget;
  filename: string;
  source_hash: string;
  source_version: string;
  recognition_run_id: string | null;
  graph_artifact_id: string | null;
  graph_content_hash: string | null;
}
export type ExpertOpinionCategory = "general" | "relationship" | "attribute" | "missing"
  | "subject_binding" | "negation" | "pruning";
export interface CreateExpertOpinion {
  request_key: string;
  context: ExpertOpinionContext;
  category: ExpertOpinionCategory;
  location: string;
  opinion: string;
  suggestion: string;
}
export interface ExpertOpinion extends Omit<CreateExpertOpinion, "request_key"> {
  opinion_id: string;
  revision: 1;
  author: string;
  author_role: string;
  created_at: string;
}
export interface ExpertOpinionList {
  context: ExpertOpinionContext;
  can_submit: boolean;
  items: ExpertOpinion[];
  total: number;
  offset: number;
  limit: number;
}
function expertOpinionQuery(target: ExpertOpinionTarget, runId?: string, offset = 0) {
  const query = new URLSearchParams({ offset: String(offset) });
  for (const [key, value] of Object.entries(target)) if (value) query.set(key, value);
  if (runId) query.set("recognition_run_id", runId);
  return query.toString();
}
export const listExpertOpinions = (target: ExpertOpinionTarget, runId?: string, offset = 0,
  signal?: AbortSignal) => fetchAPI<ExpertOpinionList>(
    `/api/report-center/expert-opinions?${expertOpinionQuery(target, runId, offset)}`, { signal });
export const createExpertOpinion = (body: CreateExpertOpinion) => fetchAPI<ExpertOpinion>(
  "/api/report-center/expert-opinions", { method: "POST", ...jsonBody(body) });
export const exportExpertOpinions = (target: ExpertOpinionTarget) => fetchAPI<{
  schema_version: "report-expert-opinions-v1"; target: ExpertOpinionTarget;
  calibration_approved: false; opinions: ExpertOpinion[];
}>(`/api/report-center/expert-opinions/export?${expertOpinionQuery(target)}`);

export interface FinderSource {
  kind: "document" | "external" | "computed";
  label: string;
  anchors: EvidenceAnchor[];
  raw_value?: unknown;
}
export interface FinderStatus {
  mode: "finder_legacy";
  template_id: string;
  source_job_id: string;
  execution_id: string | null;
  status: "not_started" | "queued" | "running" | "completed" | "failed" | "interrupted";
  stage: string | null;
  input: Record<string, unknown> | null;
  has_result: boolean;
  stale: boolean;
  error: string | null;
  counts: { nodes?: number; properties?: number; located_properties?: number };
}
export interface FinderDocument {
  execution_id: string | null;
  template_id: string;
  source_job_id: string;
  document_hash: string;
  parser_version: string;
  structure_hash: string;
  analysis_id: string;
  filename: string | null;
  content: TiptapContent;
  section_tree: WordChapterNode;
  pagination: WordPaginationMetadata;
  warnings: string[];
}
export interface FinderGraph extends Pick<FinderDocument,
  "execution_id" | "template_id" | "source_job_id" | "document_hash" | "parser_version" | "structure_hash" | "analysis_id"> {
  relationships: Relationship[];
  doc_class: DocClassification;
  counts: FinderStatus["counts"];
}
export interface RecognitionTemplateContext {
  template_id: string;
  name: string;
  version: string;
  schema_version: number;
  recognition_mode: "finder_legacy" | "ontology_guided" | "static_demo";
  finder_profile_id: string | null;
}
export interface RecognitionContext {
  document_iri: string;
  source_job_id: string;
  selected: RecognitionTemplateContext | null;
  templates: RecognitionTemplateContext[];
  selection_required: boolean;
  selection_locked: boolean;
}
const finderPath = (template: string, source: string) =>
  `/api/ast-templates/${encodeURIComponent(template)}/sources/${encodeURIComponent(source)}/finder`;
export const getTemplateFinder = (template: string, source: string, signal?: AbortSignal) =>
  fetchAPI<FinderStatus>(finderPath(template, source), { signal });
export const startTemplateFinder = (template: string, source: string,
  body: { request_key: string; expected_execution_id: string | null }) =>
  fetchAPI<FinderStatus>(finderPath(template, source), { method: "POST", ...jsonBody(body) });
export const getTemplateFinderGraph = (template: string, source: string, execution: string,
  signal?: AbortSignal) => fetchAPI<FinderGraph>(
    `${finderPath(template, source)}/graph?execution_id=${encodeURIComponent(execution)}`, { signal });
export const getTemplateFinderSource = (template: string, source: string, execution: string | null,
  signal?: AbortSignal) => fetchAPI<FinderDocument>(
    `${finderPath(template, source)}/source${execution ? `?execution_id=${encodeURIComponent(execution)}` : ""}`, { signal });
export const getFinderPdeDecision = (template: string, source: string, execution: string,
  signal?: AbortSignal) => fetchAPI<PdeConflictDecision>(
    `${finderPath(template, source)}/pde-conflict/decision?execution_id=${encodeURIComponent(execution)}`,
    { signal });
export const saveFinderPdeDecision = (template: string, source: string, execution: string,
  body: { chosen: PdeDecisionChoice; note?: string; expected_version: number }) =>
  fetchAPI<PdeConflictDecision>(
    `${finderPath(template, source)}/pde-conflict/decision?execution_id=${encodeURIComponent(execution)}`,
    { method: "POST", ...jsonBody(body) });
export const getRecognitionContext = (documentIri: string, templateId?: string | null,
  signal?: AbortSignal) => fetchAPI<RecognitionContext>(
    `/api/ast-templates/recognition-context?${new URLSearchParams({ document_iri: documentIri,
      ...(templateId ? { template_id: templateId } : {}) })}`, { signal });

export interface HarnessSnapshot {
  session_id: string;
  sequence: number;
  call: {
    call_id: string; stage: "discovery" | "verification"; model: string;
    task_kind?: "recognition" | "record_discovery" | "property_disambiguation";
    subject_label: string | null; predicate_label: string | null; input_tokens: number;
    member_count?: number;
    members?: { task_id: string; predicate_iri: string; predicate_label: string }[];
    status: string; started_at: string; usage: Record<string, unknown> | null;
  } | null;
  output: string;
  thinking: string;
  truncated: ("output" | "thinking")[];
  operations: {
    id: string; kind: "model" | "tool" | "validation" | "graph"; name: string;
    status: string; started_at: string; elapsed_ms: number | null;
    arguments?: { text: string; truncated: boolean } | null;
    result?: { text: string; truncated: boolean } | null;
  }[];
  tool_counts: Record<string, number>;
  updated_at: string | null;
}

export interface HarnessAttributeDisambiguation {
  task_id: string;
  field_id: string;
  record_id: string;
  label: string | null;
  value: string | null;
  candidate_count: number | null;
  attribute_status: "pending" | "resolved" | "unresolved";
  work_status: "pending" | "active" | "examined" | "incomplete";
  reason_code: string | null;
  disambiguation_attempts: number;
}

export interface DocumentHarness {
  recognition_run_id: string;
  configuration: {
    model: string | null; model_revision: string | null; api_protocol: string | null;
    mock_enabled: boolean; vocabulary_enabled: boolean;
    request_budget: Record<string, number> | null;
  };
  snapshot: HarnessSnapshot | null;
  attribute_disambiguations?: HarnessAttributeDisambiguation[];
}

export interface HarnessPredicate {
  iri: string; label: string; kind: string; description?: string;
  min_count?: number | null; max_count?: number | null; multiplicity?: string;
  datatype_iris?: string[]; canonical_unit?: string | null;
  range_classes?: { iri: string; label: string }[];
  range_class_iris?: string[]; constraint_status?: string;
}

interface HarnessCardConstraints {
  quantity_policies: {
    predicate_iri: string; allowed_forms: string[]; endpoint_role: string | null;
    allowed_target_units: string[]; unit_requirement: string; declaration_ref: string;
  }[];
  identity_keys: { class_iri: string; property_iris: string[]; namespace: string | null; scope: string; declaration_ref: string }[];
  unsupported_constraints: { predicate_iri: string; construct: string; reason_code: string }[];
}

export interface HarnessPredicateSchemaCard extends HarnessCardConstraints {
  schema_card_id: string;
  class_iris: string[];
  predicates: HarnessPredicate[];
}

export interface HarnessRecordSchemaCard {
  kind: "record_discovery";
  schema_card_id: string;
  ontology_snapshot_id: string;
  analysis_scope_ref: string;
  class_cards: (HarnessCardConstraints & {
    class_iri: string;
    label: string;
    properties: HarnessPredicate[];
  })[];
}

export type HarnessSchemaCard = HarnessPredicateSchemaCard | HarnessRecordSchemaCard;

export interface HarnessContext {
  recognition_run_id: string;
  call_id: string;
  request: { instructions: string; input: Record<string, unknown>[]; text?: { format: unknown }; [key: string]: unknown };
  schema_card: HarnessSchemaCard | { members: { task_id: string; card: HarnessSchemaCard }[] };
  class_labels?: Record<string, string>;
}

export const getDocumentHarness = (runId: string, signal?: AbortSignal) =>
  fetchAPI<DocumentHarness>(`${documentRunPath(runId)}/harness`, { signal });

export const getDocumentHarnessContext = (runId: string, callId: string, signal?: AbortSignal) =>
  fetchAPI<HarnessContext>(`${documentRunPath(runId)}/harness/context?call_id=${encodeURIComponent(callId)}`, { signal });

// Mapped Mock sources are read-only candidates, separate from saved entities.
export interface LookupKeyGroup { property_iris: string[]; scope_property_iris: string[]; }
export interface MockQueryConfig {
  label_path: string | null; entity_iri_path?: string | null; class_path?: string | null;
  identifier_namespace?: string | null; lookup_key_groups: LookupKeyGroup[];
}
export interface EntityQueryIssue {
  code: string; message: string; property_iri?: string | null; record_id?: string | null;
}
export interface QueryPropertyDefinition {
  property_iri: string; label: string; datatype_iris: string[];
}
export interface EntitySourceMapping {
  id: string; class_iri: string; class_label: string; mapping_revision: string;
  query_config: MockQueryConfig | null; queryable: boolean; issues: EntityQueryIssue[];
  properties: QueryPropertyDefinition[]; mapped_property_iris: string[];
  identity_properties: (QueryPropertyDefinition & { available: boolean })[];
  identity_key_groups: { property_iris: string[]; available: boolean; unavailable_property_iris: string[] }[];
}
export interface MappedEntitySource {
  source_system: string; dataset: string; label: string; source_kind: "mock";
  fields: { source_path: string; label: string; source_datatype: string; multiple: boolean }[];
  field_catalog_complete: boolean; issues: EntityQueryIssue[]; mappings: EntitySourceMapping[];
}
export interface InitialMockMapping extends MappingInput {
  class_iri: string; property_bindings: PropertyBindingInput[]; available: boolean;
}
export interface EntitySources {
  sources: MappedEntitySource[]; initial_mappings: InitialMockMapping[];
}
export interface MappedEntityQuery {
  query_id: string; class_iri: string; include_subclasses?: boolean; mapping_ids?: string[];
  name?: { value: string; match: "exact" | "contains" } | null;
  property_filters: { property_iri: string; value: string | number | boolean; datatype_iri: string }[];
  limit?: number;
  offset?: number;
}
export interface MappedEntityCandidate {
  record_ref: { source_system: string; dataset: string; record_id: string };
  record_version: string; mapping_id: string; mapping_revision: string; source_kind: "mock";
  source_entity_iri: string | null; class_iri: string; label: string;
  properties: { property_iri: string; values: { value: string | number | boolean;
    datatype_iri: string; raw_value: string | number | boolean; source_path: string;
    source_index: number | null }[] }[];
  matches: { kind: string; property_iri?: string | null }[];
  matched_lookup_groups: number[]; identifier_namespace: string | null;
  business_scope_status: "provided" | "unspecified"; identity_status: "not_checked";
  issues: EntityQueryIssue[];
}
export interface MappedEntityQueryResult {
  query_id: string; outcome: "matches" | "no_match" | "unresolved"; complete: boolean;
  total: number | null; next_offset: number | null;
  truncated: boolean; candidates: MappedEntityCandidate[]; issues: EntityQueryIssue[];
  sources: { mapping_id: string; status: string; issues: EntityQueryIssue[] }[];
}
export const getEntitySources = (signal?: AbortSignal) =>
  fetchAPI<EntitySources>("/api/entities/sources", { signal });
export const queryMappedEntities = (queries: MappedEntityQuery[], signal?: AbortSignal) =>
  fetchAPI<{ results: MappedEntityQueryResult[] }>("/api/entities/query", {
    method: "POST", ...jsonBody({ queries }), signal,
  });
